import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  useMediaQuery,
  Grid,
  Card,
  CardMedia,
  LinearProgress,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY, FONT_FAMILY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import AppToast from "../../components/AppToast";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, startOfMonth, endOfMonth, parseISO } from "date-fns";
import PhotoUploadWithDrawing from "../../components/siteCheck/PhotoUploadWithDrawing";
import useFormPersistence from "../../hooks/useFormPersistence";

// Custom Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder, height = "161px" }) => {
  const [isFocused, setIsFocused] = useState(false);
  const editorRef = React.useRef(null);

  const handleTextChange = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  };

  const handleFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    handleTextChange();
    editorRef.current.focus();
  };

  const handleAlignment = (alignment) => {
    const alignmentMap = {
      left: "justifyLeft",
      center: "justifyCenter",
      right: "justifyRight",
      justify: "justifyFull",
    };
    document.execCommand(alignmentMap[alignment]);
    handleTextChange();
    editorRef.current.focus();
  };

  const handleList = (listType) => {
    const command =
      listType === "unordered" ? "insertUnorderedList" : "insertOrderedList";
    document.execCommand(command);
    handleTextChange();
    editorRef.current.focus();
  };

  const handleClearFormat = () => {
    // Select all text first
    document.execCommand("selectAll");
    // Remove all formatting
    document.execCommand("removeFormat");
    // Clear any remaining styles
    document.execCommand("unlink");
    document.execCommand("formatBlock", false, "p");
    handleTextChange();
    editorRef.current.focus();
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => {
    setIsFocused(false);
    handleTextChange();
  };

  // Set initial content
  React.useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 1,
          p: 1,
          border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
          borderRadius: "8px 8px 0 0",
          backgroundColor: "#f5f5f5",
          flexWrap: "wrap",
        }}
      >
        {/* Bold, Italic, Underline */}
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleFormat("bold")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Bold"
          >
            <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>B</Typography>
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleFormat("italic")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              fontStyle: "italic",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Italic"
          >
            I
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleFormat("underline")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              textDecoration: "underline",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Underline"
          >
            U
          </IconButton>
        </Box>

        {/* Alignment */}
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleAlignment("left")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Align Left"
          >
            <Icon name="alignLeft" size={16} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleAlignment("center")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Align Center"
          >
            <Icon name="alignCenter" size={16} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleAlignment("right")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Align Right"
          >
            <Icon name="alignRight" size={16} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleAlignment("justify")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Justify"
          >
            <Icon name="alignJustify" size={16} color={COLORS.TEXT} />
          </IconButton>
        </Box>

        {/* Lists */}
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={() => handleList("unordered")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Bulleted List"
          >
            <Icon name="listUnordered" size={16} color={COLORS.TEXT} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleList("ordered")}
            sx={{
              width: 36,
              height: 36,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            title="Numbered List"
          >
            <Icon name="listOrdered" size={16} color={COLORS.TEXT} />
          </IconButton>
        </Box>

        {/* Clear Formatting */}
        <Box display="flex" gap={0.5}>
          <Button
            size="small"
            onClick={handleClearFormat}
            sx={{
              height: 36,
              fontSize: 12,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              color: COLORS.PRIMARY,
              "&:hover": { backgroundColor: "#f8f8f8" },
            }}
          >
            Clear Format
          </Button>
          <Button
            size="small"
            onClick={() => {
              if (editorRef.current) {
                editorRef.current.innerHTML = "";
                onChange("");
              }
            }}
            sx={{
              height: 36,
              fontSize: 12,
              backgroundColor: "#fff",
              border: `1px solid ${COLORS.ERROR}`,
              color: COLORS.ERROR,
              "&:hover": { backgroundColor: `${COLORS.ERROR}15` },
            }}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {/* Editor Area */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleTextChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        sx={{
          border: `1px solid ${isFocused ? COLORS.PRIMARY : COLORS.LIGHT_SECONDARY}`,
          borderRadius: "0 0 8px 8px",
          minHeight: height,
          padding: "12px",
          fontFamily: FONT_FAMILY,
          fontSize: "14px",
          color: COLORS.PRIMARY,
          outline: "none",
          overflowY: "auto",
          backgroundColor: "#fff",
          "&:empty:before": {
            content: `"${placeholder || "Start typing here..."}"`,
            color: COLORS.LIGHT_SECONDARY,
          },
          "&:focus": {
            borderColor: COLORS.PRIMARY,
          },
          "& p": {
            margin: "0 0 8px 0",
            color: COLORS.PRIMARY,
          },
          "& ul, & ol": {
            margin: "0 0 8px 0",
            paddingLeft: "20px",
            color: COLORS.PRIMARY,
          },
          "& li": {
            color: COLORS.PRIMARY,
          },
        }}
      />
    </Box>
  );
};

// Overall Progress Bar Chart Component
const OverallProgressBarChart = ({ progressItems, label }) => {
  // Use a minimum bar height so small values are still visible
  const minBarHeight = 20; // Minimum height for visibility
  const maxBarHeight = 120; // Maximum height

  // Calculate bar heights (ensure visibility for small values)
  const calculateHeight = (value) => {
    const baseHeight = (value / 100) * maxBarHeight;
    return Math.max(baseHeight, minBarHeight);
  };

  // Modern colors for bars
  const chartColors = [
    "#421A6E", // Primary
    "#2196F3", // Blue
    "#4CAF50", // Green
    "#FF9800", // Orange
    "#E91E63", // Pink
    "#9C27B0", // Purple
    "#00BCD4", // Cyan
    "#FFC107", // Amber
  ];

  return (
    <Box
      sx={{
        width: "100%",
        mt: 1,
        p: 2,
        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
        borderRadius: "8px",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: COLORS.PRIMARY,
          fontFamily: FONT_FAMILY,
          display: "block",
          mb: 2,
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          height: maxBarHeight + 35,
          gap: 1.5,
          overflowX: "auto",
          pb: 1,
        }}
      >
        {/* Overall Progress Bar (Always 100% contributing) */}
        <Box
          sx={{
            minWidth: "50px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              mb: 0.5,
              color: COLORS.PRIMARY,
              fontFamily: FONT_FAMILY,
              fontSize: "10px",
              fontWeight: "bold",
              textAlign: "center",
              height: "24px",
              display: "flex",
              alignItems: "center",
            }}
          >
            Overall
          </Typography>
          <Box
            sx={{
              width: "40px",
              height: `${maxBarHeight}px`,
              backgroundColor: "#9C27B0",
              borderRadius: "4px 4px 0 0",
              transition: "height 0.5s ease-in-out",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Typography
              sx={{
                color: "white",
                fontSize: "10px",
                fontWeight: "bold",
                mb: 0.5,
              }}
            >
              100%
            </Typography>
          </Box>
        </Box>

        {/* Dynamic Bars based on progressItems */}
        {progressItems.map((item, index) => {
          const height = calculateHeight(parseFloat(item.percentage) || 0);
          const color = chartColors[index % chartColors.length];

          return (
            <Box
              key={index}
              sx={{
                minWidth: "50px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  mb: 0.5,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  fontSize: "10px",
                  fontWeight: "bold",
                  textAlign: "center",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  width: "60px",
                  lineHeight: "1.1",
                }}
              >
                {item.category}
              </Typography>
              <Box
                sx={{
                  width: "40px",
                  height: `${height}px`,
                  backgroundColor: color,
                  borderRadius: "4px 4px 0 0",
                  transition: "height 0.5s ease-in-out",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontSize: "10px",
                    fontWeight: "bold",
                    mb: 0.5,
                  }}
                >
                  {item.percentage}%
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* X-axis line */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          mt: 0.5,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "2px",
            backgroundColor: COLORS.LIGHT_SECONDARY,
          }}
        />
      </Box>
    </Box>
  );
};

// Bar Chart Component for Planned vs Achieved - UPDATED
// Bar Chart Component for Planned vs Achieved - ENHANCED VERSION
const BarChart = ({ planValue, actualValue, label }) => {
  // Use a minimum bar height so small values are still visible
  const minBarHeight = 20; // Minimum height for visibility
  const maxBarHeight = 120; // Maximum height

  // Calculate bar heights (ensure visibility for small values)
  const calculateHeight = (value) => {
    const baseHeight = (value / 100) * maxBarHeight;
    return Math.max(baseHeight, minBarHeight);
  };

  const planHeight = calculateHeight(planValue);
  const actualHeight = calculateHeight(actualValue);

  return (
    <Box
      sx={{
        width: "100%",
        mt: 1,
        p: 2,
        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
        borderRadius: "8px",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: COLORS.PRIMARY,
          fontFamily: FONT_FAMILY,
          display: "block",
          mb: 2,
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          height: maxBarHeight + 30,
          gap: 3,
        }}
      >
        {/* Plan Bar */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              mb: 0.5,
              color: COLORS.PRIMARY,
              fontFamily: FONT_FAMILY,
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            Plan
          </Typography>
          <Box
            sx={{
              width: "50px",
              height: `${planHeight}px`,
              backgroundColor: COLORS.PRIMARY,
              borderRadius: "4px 4px 0 0",
              transition: "height 0.5s ease-in-out",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Typography
              sx={{
                color: "white",
                fontSize: "12px",
                fontWeight: "bold",
                mb: 0.5,
              }}
            >
              {planValue}%
            </Typography>
          </Box>
        </Box>

        {/* Actual Bar */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              mb: 0.5,
              color: actualValue >= planValue ? "#4CAF50" : "#f44336",
              fontFamily: FONT_FAMILY,
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            Actual
          </Typography>
          <Box
            sx={{
              width: "50px",
              height: `${actualHeight}px`,
              backgroundColor: actualValue >= planValue ? "#4CAF50" : "#f44336",
              borderRadius: "4px 4px 0 0",
              transition: "height 0.5s ease-in-out",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Typography
              sx={{
                color: "white",
                fontSize: "12px",
                fontWeight: "bold",
                mb: 0.5,
              }}
            >
              {actualValue}%
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* X-axis line */}
      {/* X-axis line with labels */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          mt: 0.5,
        }}
      >
        {/* The line */}
        <Box
          sx={{
            width: "100%",
            height: "2px",
            backgroundColor: COLORS.LIGHT_SECONDARY,
          }}
        />

        {/* Labels container */}
        {/* <Box sx={{
    position: 'absolute',
    width: '100%',
    top: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    px: '10px',
  }}>
    <Typography sx={{
      fontSize: '10px',
      color: COLORS.SECONDARY,
      fontFamily: FONT_FAMILY,
    }}>
      0%
    </Typography>
    <Typography sx={{
      fontSize: '10px',
      color: COLORS.SECONDARY,
      fontFamily: FONT_FAMILY,
    }}>
      100%
    </Typography>
  </Box> */}
      </Box>

      {/* Variance with better styling */}
      <Box
        sx={{
          mt: 2,
          p: 1,
          backgroundColor:
            actualValue >= planValue
              ? "rgba(76, 175, 80, 0.1)"
              : "rgba(244, 67, 54, 0.1)",
          borderRadius: "4px",
          textAlign: "center",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: actualValue >= planValue ? "#4CAF50" : "#f44336",
            fontFamily: FONT_FAMILY,
            fontWeight: "bold",
            fontSize: "12px",
          }}
        >
          Variance: {actualValue - planValue}% (
          {actualValue >= planValue ? "Ahead" : "Behind"})
        </Typography>
      </Box>
    </Box>
  );
};

export default function AddMPR() {
  const { projectId } = useParams();
  const { token, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Get month data from navigation state
  const {
    monthStartDate: initialMonthStartDate,
    monthEndDate: initialMonthEndDate,
    projectName: propProjectName,
    selectedMonth: initialSelectedMonth,
  } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");

  // Month date state
  const [monthStartDate, setMonthStartDate] = useState(
    initialMonthStartDate ? new Date(initialMonthStartDate) : null,
  );
  const [monthEndDate, setMonthEndDate] = useState(
    initialMonthEndDate ? new Date(initialMonthEndDate) : null,
  );
  const [selectedMonth, setSelectedMonth] = useState(
    initialSelectedMonth ? new Date(initialSelectedMonth) : new Date(),
  );
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  // MPR data state with persistence
  const persistenceKey = `mpr-form-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}`;
  const [mprData, setMprData, clearPersistence] = useFormPersistence(persistenceKey, {
    executiveSummary: {
      overallStatus: "",
      keyAchievements: "",
      majorConcerns: "",
    },
    overallProgress: [
      { category: "Structural", percentage: "" },
      { category: "MEP", percentage: "" },
      { category: "Finishing", percentage: "" },
      { category: "External Development", percentage: "" },
    ],
    plannedVsAchieved: [],
    manpowerSummary: [],
    plantMachinery: [],
    materialManagement: [],
    qualityControl: {
      materialTestingSummary: [],
      inspectionAndNCRSummary: [],
    },
    safetyHSE: [],
    financialStatus: [],
    drawingsApprovals: {
      gfcDrawingReceived: [],
      pendingDrawing: [],
    },
    keyIssues: [],
    reports: [],
    decisionsRequired: [],
    lookAheadPlan: [],
    rfiLogs: [],
    photos: [],
  });

  // Form states for dynamic sections
  const [showExecutiveSummaryForm, setShowExecutiveSummaryForm] =
    useState(false);
  const [showOverallProgressForm, setShowOverallProgressForm] = useState(false);
  const [showPlannedVsAchievedForm, setShowPlannedVsAchievedForm] =
    useState(false);
  const [showManpowerForm, setShowManpowerForm] = useState(false);
  const [showPlantMachineryForm, setShowPlantMachineryForm] = useState(false);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [showMaterialTestForm, setShowMaterialTestForm] = useState(false);
  const [showInspectionNCRForm, setShowInspectionNCRForm] = useState(false);
  const [showSafetyForm, setShowSafetyForm] = useState(false);
  const [showFinancialForm, setShowFinancialForm] = useState(false);
  const [showDrawingsForm, setShowDrawingsForm] = useState(false);
  const [showIssuesForm, setShowIssuesForm] = useState(false);
  const [showDecisionsForm, setShowDecisionsForm] = useState(false);
  const [showLookAheadForm, setShowLookAheadForm] = useState(false);
  const [showRFIForm, setShowRFIForm] = useState(false);

  const [editingEntry, setEditingEntry] = useState({
    section: null,
    index: null,
    data: null,
  });

  // New entry states with persistence
  const executiveSummaryKey = `mpr-exec-summary-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}`;
  const [newExecutiveSummary, setNewExecutiveSummary, clearNewExecutiveSummary] = useFormPersistence(executiveSummaryKey, {
    overallStatus: "",
    keyAchievements: "",
    majorConcerns: "",
  });

  const overallProgressKey = `mpr-overall-progress-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}`;
  const [newOverallProgress, setNewOverallProgress, clearNewOverallProgress] = useFormPersistence(overallProgressKey, {
    category: "",
    percentage: "",
  });

  const [isOverallProgressEditMode, setIsOverallProgressEditMode] =
    useState(true);
  const [overallProgressTotal, setOverallProgressTotal] = useState(0);
  
  const plannedAchievedPersistenceKey = `mpr-planned-achieved-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'plannedVsAchieved' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newPlannedVsAchieved, setNewPlannedVsAchieved, clearNewPlannedVsAchieved] = useFormPersistence(plannedAchievedPersistenceKey, {
    activity: "",
    plannedDate: "",
    actualDate: "",
    variance: "",
    remarkStatus: "",
  });

  const manpowerKey = `mpr-manpower-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'manpowerSummary' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newManpower, setNewManpower, clearNewManpower] = useFormPersistence(manpowerKey, {
    category: "",
    averageManpower: "",
    peakManpower: "",
    remarks: "",
  });

  const plantMachineryKey = `mpr-plant-machinery-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'plantMachinery' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newPlantMachinery, setNewPlantMachinery, clearNewPlantMachinery] = useFormPersistence(plantMachineryKey, {
    equipmentDetails: "",
    capacity: "",
    quantity: "",
    utilizationEfficiency: "",
    remarks: "",
  });

  const materialKey = `mpr-material-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'materialManagement' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newMaterial, setNewMaterial, clearNewMaterial] = useFormPersistence(materialKey, {
    materialName: "",
    received: "",
    consumed: "",
    remarks: "",
  });

  const materialTestKey = `mpr-material-test-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'materialTestingSummary' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newMaterialTest, setNewMaterialTest, clearNewMaterialTest] = useFormPersistence(materialTestKey, {
    testDescription: "",
    testConductedDate: "",
    reportId: "",
    result: "",
    status: "",
    remarks: "",
  });

  const inspectionNCRKey = `mpr-inspection-ncr-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'inspectionAndNCRSummary' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newInspectionNCR, setNewInspectionNCR, clearNewInspectionNCR] = useFormPersistence(inspectionNCRKey, {
    ncrNo: "",
    date: "",
    category: "",
    description: "",
    status: "",
  });

  const safetyKey = `mpr-safety-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'safetyHSE' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newSafety, setNewSafety, clearNewSafety] = useFormPersistence(safetyKey, {
    date: "",
    parameter: "",
    description: "",
    status: "Open",
    remarks: "",
  });

  const financialKey = `mpr-financial-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'financialStatus' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newFinancial, setNewFinancial, clearNewFinancial] = useFormPersistence(financialKey, {
    description: "",
    amountSubmitted: "",
    amountCertified: "",
    paymentReceived: "",
    remarks: "",
  });

  const drawingsKey = `mpr-drawings-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'drawingsApprovals' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newDrawings, setNewDrawings, clearNewDrawings] = useFormPersistence(drawingsKey, {
    // GFC Drawing Received
    gfcCategory: "",
    gfcDrawingType: "",
    gfcDrawingNo: "",
    gfcRevision: "",
    gfcReceivedDate: "",
    gfcRemarkStatus: "",
    // Pending Drawings
    pendingCategory: "",
    pendingDrawingType: "",
    pendingExpectedDate: "",
    pendingCurrentStatus: "",
    pendingRemark: "",
  });

  const issuesKey = `mpr-issues-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'keyIssues' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newIssues, setNewIssues, clearNewIssues] = useFormPersistence(issuesKey, {
    issueDescription: "",
    delayReasons: "",
    mitigationMeasures: "",
    impact: "",
    remarks: "",
  });

  const decisionsKey = `mpr-decisions-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'decisionsRequired' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newDecisions, setNewDecisions, clearNewDecisions] = useFormPersistence(decisionsKey, {
    decisionTopic: "",
    requiredByDate: "",
    responsibility: "",
    otherResponsibility: "",
    remarks: "",
  });

  const rfiKey = `mpr-rfi-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'rfiLogs' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newRFI, setNewRFI, clearNewRFI] = useFormPersistence(rfiKey, {
    rfiNo: "",
    description: "",
    issueDate: "",
    status: "Open",
    expectedDate: "",
    remarks: "",
  });

  const lookAheadKey = `mpr-lookahead-draft-${projectId}-${monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month"}-${editingEntry?.section === 'lookAheadPlan' ? editingEntry.data?._id || editingEntry.index : 'new'}`;
  const [newLookAhead, setNewLookAhead, clearNewLookAhead] = useFormPersistence(lookAheadKey, {
    activity: "",
    startDate: "",
    endDate: "",
    resourceRequirement: "",
    remarks: "",
  });

  const [uploadedReports, setUploadedReports] = useState([]);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [showPhotoUploadDialog, setShowPhotoUploadDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [averageManpowerData, setAverageManpowerData] = useState([]);
  const [deletedCategories, setDeletedCategories] = useState([]); // Track deleted categories
  const [inlineEditingIndex, setInlineEditingIndex] = useState(null); // For inline editing

  // Predefined labor types
  const predefinedLaborTypes = [
    "Engineer",
    "Supervisor",
    "Unskilled Labour",
    "Carpenter",
    "Subcontractor",
  ];

  const responsibilityOptions = [
    "Structural Consultant",
    "MEP Consultant",
    "Architect",
    "Interior Designer",
    "Developer / Builder",
    "Individual",
    "Other Consultant",
    "Other",
  ];

  // Restore images from persistence on load
  useEffect(() => {
    if (mprData.photos && mprData.photos.length > 0 && uploadedPhotos.length === 0) {
      const restoredPhotos = mprData.photos.map(photo => {
        if (photo.imageUrl && photo.imageUrl.startsWith('data:image')) {
          try {
            const base64Data = photo.imageUrl.split(',')[1];
            const binaryData = atob(base64Data);
            const arrayBuffer = new ArrayBuffer(binaryData.length);
            const uint8Array = new Uint8Array(arrayBuffer);
            for (let i = 0; i < binaryData.length; i++) {
              uint8Array[i] = binaryData.charCodeAt(i);
            }
            const blob = new Blob([uint8Array], { type: photo.type || 'image/png' });
            const file = new File([blob], photo.name || `photo_${Date.now()}.png`, { type: photo.type || 'image/png' });
            return { ...photo, file };
          } catch (e) {
            console.error("Error restoring photo from persistence:", e);
            return photo;
          }
        }
        return photo;
      });
      setUploadedPhotos(restoredPhotos);
    }
  }, [mprData.photos]);

  // Auto-show photo upload dialog if draft exists
  useEffect(() => {
    if (persistenceKey && !showPhotoUploadDialog) {
      const photoDraftKey = `mpr-photo-draft-${persistenceKey}`;
      const draft = localStorage.getItem(photoDraftKey);
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          if (parsed && (parsed.title || parsed.preview || (parsed.shapes && parsed.shapes.length > 0))) {
            setShowPhotoUploadDialog(true);
          }
        } catch (e) {}
      }
    }
  }, [persistenceKey]);

  // Drawing category options
  const drawingCategoryOptions = [
    "Structural Consultant",
    "MEP Consultant",
    "Architect",
    "Interior Designer",
    "Developer / Builder",
    "Individual",
    "Other Consultant",
    "Other",
  ];

  // Format date functions
  const formatMonthForHeader = (date) => {
    if (!date) return "";
    return format(date, "MMMM yyyy");
  };

  // Fetch average manpower data
  const fetchAverageManpower = async () => {
    if (!monthStartDate || !projectId || !token) return;

    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      const month = monthStartDate.getMonth() + 1;
      const year = monthStartDate.getFullYear();

      const response = await axios.get(
        `${url}/api/mpr/average-manpower?projectId=${projectId}&month=${month}&year=${year}`,
        config,
      );

      if (response.data.manpowerSummaries) {
        setAverageManpowerData(response.data.manpowerSummaries);

        // Only update missing values, don't replace existing data
        setMprData((prev) => {
          const existingManpower = prev.manpowerSummary || [];

          // Only update entries that don't exist in current data
          const updatedManpower = existingManpower.map((existingItem) => {
            const apiItem = response.data.manpowerSummaries.find(
              (backendItem) =>
                backendItem.category.toLowerCase() ===
                existingItem.category.toLowerCase(),
            );

            if (apiItem) {
              return {
                ...existingItem,
                averageManpower:
                  apiItem.averageManpower || existingItem.averageManpower,
                peakManpower:
                  existingItem.peakManpower || apiItem.peakManpower || 0,
                remarks: existingItem.remarks || apiItem.remarks || "",
              };
            }
            return existingItem;
          });

          // Add new entries from API that don't exist in current data AND weren't deleted
          const newEntries = response.data.manpowerSummaries
            .filter(
              (apiItem) =>
                !existingManpower.some(
                  (existingItem) =>
                    existingItem.category &&
                    existingItem.category.toLowerCase() ===
                      apiItem.category.toLowerCase(),
                ) &&
                !deletedCategories.includes(apiItem.category.toLowerCase()),
            )
            .map((apiItem) => ({
              category: apiItem.category,
              averageManpower: apiItem.averageManpower || 0,
              peakManpower: apiItem.peakManpower || 0,
              remarks: apiItem.remarks || "",
            }));

          return {
            ...prev,
            manpowerSummary: [...updatedManpower, ...newEntries],
          };
        });
      }
    } catch (err) {
      console.error("Error fetching average manpower:", err);
      // If API fails, just use existing data without changes
      // Don't initialize with predefined types since we want to rely on API data
    }
  };

  // Handle month selection
  const handleMonthSelect = (date) => {
    if (!date) return;

    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);

    // First clear the data
    resetFormData();

    // Then update the month, which will trigger the fetch useEffect
    setSelectedMonth(date);
    setMonthStartDate(monthStart);
    setMonthEndDate(monthEnd);
    setShowMonthPicker(false);

    showToast(
      "success",
      "Month Selected",
      `Month: ${formatMonthForHeader(date)}`,
    );
  };

  // Reset form data
  const resetFormData = () => {
    setMprData({
      executiveSummary: {
        overallStatus: "",
        keyAchievements: "",
        majorConcerns: "",
      },
      overallProgress: [
        { category: "Structural", percentage: "0" },
        { category: "MEP", percentage: "0" },
        { category: "Finishing", percentage: "0" },
        { category: "External Development", percentage: "0" },
      ],
      plannedVsAchieved: [],
      manpowerSummary: [], // This will be populated by fetchAverageManpower
      plantMachinery: [],
      materialManagement: [],
      qualityControl: {
        materialTestingSummary: [],
        inspectionAndNCRSummary: [],
      },
      safetyHSE: [],
      financialStatus: [],
      drawingsApprovals: {
        gfcDrawingReceived: [],
        pendingDrawing: [],
      },
      keyIssues: [],
      reports: [],
      decisionsRequired: [],
      lookAheadPlan: [],
      photos: [],
    });

    setUploadedReports([]);
    setUploadedPhotos([]);
  };

  //   }
  // }, [selectedMonth]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };

        // Fetch project details
        const projectResponse = await axios.get(
          `${url}/api/inhouse-projects/fetch-by/${projectId}`,
          config,
        );

        if (projectResponse.data.project) {
          setProject(projectResponse.data.project);

          // Fetch companies for PMC dropdown
          const companiesResponse = await axios.get(
            `${url}/api/company/fetch`,
            config,
          );

          if (companiesResponse.data.data) {
            setCompanies(companiesResponse.data.data);
            if (companiesResponse.data.data.length > 0) {
              setSelectedCompany(companiesResponse.data.data[0]._id);
            }
          }

          // Initialize month dates if provided from props
          if (initialMonthStartDate && initialMonthEndDate) {
            const startDate = new Date(initialMonthStartDate);
            const endDate = new Date(initialMonthEndDate);
            setMonthStartDate(startDate);
            setMonthEndDate(endDate);

            // Set selected month
            if (initialSelectedMonth) {
              setSelectedMonth(new Date(initialSelectedMonth));
            } else {
              setSelectedMonth(startDate);
            }
          } else {
            // If no month provided, set to current month
            const today = new Date();
            const monthStart = startOfMonth(today);
            const monthEnd = endOfMonth(today);
            setMonthStartDate(monthStart);
            setMonthEndDate(monthEnd);
            setSelectedMonth(today);
          }
        }
      } catch (err) {
        console.error("Error fetching project data:", err);
        setError("Failed to load project data. Please try again.");
        showToast("error", "Error", "Failed to load project data.");
        setLoading(false);
      }
    };

    fetchData();
  }, [
    projectId,
    token,
    initialMonthStartDate,
    initialMonthEndDate,
    initialSelectedMonth,
  ]);

  // ADD a separate useEffect to fetch MPR data when month dates are set:
  useEffect(() => {
    const fetchMPRData = async () => {
      if (!monthStartDate || !monthEndDate || !projectId || !token) return;

      try {
        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };

        const monthStartStr = format(monthStartDate, "yyyy-MM-dd");
        const monthEndStr = format(monthEndDate, "yyyy-MM-dd");

        // Fetch MPR for the selected month and project
        const response = await axios.get(
          `${url}/api/mpr/fetch-by-project/${projectId}?monthStartDate=${monthStartStr}&monthEndDate=${monthEndStr}`,
          config,
        );

        if (response.data.mprs && response.data.mprs.length > 0) {
          const mpr = response.data.mprs[0];

          // Check if the MPR overlaps with the selected month range
          const mprStart = parseISO(mpr.monthStartDate);
          const mprEnd = parseISO(mpr.monthEndDate);
          const selectedStart = monthStartDate;
          const selectedEnd = monthEndDate;

          // MPR overlaps if it starts before or on selected end date AND ends after or on selected start date
          const overlaps = mprStart <= selectedEnd && mprEnd >= selectedStart;

          if (overlaps) {
            // Reset form data BEFORE populating
            resetFormData();
            // Populate form with existing MPR data
            populateMPRData(mpr);
            // Also fetch average manpower to ensure predefined types have correct values
            fetchAverageManpower();
            showToast(
              "info",
              "MPR Found",
              "Existing MPR data loaded for this month.",
            );
          } else {
            // If MPR does not overlap, just fetch average manpower
            fetchAverageManpower();
            showToast(
              "info",
              "New MPR",
              "No existing MPR found for this month.",
            );
          }
        } else {
          // If no MPR data is found, fetch average manpower
          fetchAverageManpower();
          showToast("info", "New MPR", "No existing MPR found for this month.");
        }
      } catch (err) {
        console.error("Error fetching MPR data:", err);
        // On error, still fetch average manpower
        fetchAverageManpower();
      } finally {
        setLoading(false);
      }
    };

    if (project && monthStartDate && monthEndDate) {
      fetchMPRData();
    }
  }, [monthStartDate, monthEndDate, projectId, token]);

  // Populate form data from existing MPR
  const populateMPRData = (mpr) => {
    // Create a new object to avoid multiple state updates
    const populatedData = {
      executiveSummary: {
        overallStatus: "",
        keyAchievements: "",
        majorConcerns: "",
      },
      overallProgress: [
        { category: "Structural", percentage: "0" },
        { category: "MEP", percentage: "0" },
        { category: "Finishing", percentage: "0" },
        { category: "External Development", percentage: "0" },
      ],
      plannedVsAchieved: [],
      manpowerSummary: [],
      plantMachinery: [],
      materialManagement: [],
      qualityControl: {
        materialTestingSummary: [],
        inspectionAndNCRSummary: [],
      },
      safetyHSE: [],
      drawingsApprovals: {
        gfcDrawingReceived: [],
        pendingDrawing: [],
      },
      keyIssues: [],
      reports: [],
      decisionsRequired: [],
      lookAheadPlan: [],
      photos: [],
    };

    // Convert executiveSummaries to separate fields
    if (mpr.executiveSummaries && mpr.executiveSummaries.length > 0) {
      // Get the first executive summary item
      const firstItem = mpr.executiveSummaries[0];
      populatedData.executiveSummary = {
        overallStatus: firstItem.overallStatus || "",
        keyAchievements: firstItem.keyAchievements || "",
        majorConcerns: firstItem.majorConcerns || "",
      };
    }

    // Convert overallProgress to array format with default category fallback and padding for first 4 items
    const DEFAULT_CATEGORIES = [
      "Structural",
      "MEP",
      "Finishing",
      "External Development",
    ];
    let backendItems = [];
    if (mpr.overallProgress && mpr.overallProgress.length > 0) {
      backendItems = mpr.overallProgress.map((item, index) => ({
        category:
          index < 4 && !item.category
            ? DEFAULT_CATEGORIES[index]
            : item.category || "",
        percentage: (item.percentage || 0).toString(),
      }));
    }

    // Ensure we have at least the 4 default categories
    if (backendItems.length < 4) {
      const existingCats = backendItems.map((item) =>
        item.category.toLowerCase(),
      );
      const missingDefaults = DEFAULT_CATEGORIES.filter(
        (cat) => !existingCats.includes(cat.toLowerCase()),
      );

      // If we have some backend items, just append missing ones, or if none, use all defaults
      populatedData.overallProgress = [
        ...backendItems,
        ...missingDefaults.map((cat) => ({ category: cat, percentage: "" })),
      ];

      // Sort to keep defaults at top if possible, or just slice to ensure exactly 4 if that's what's preferred.
      // Actually, let's just make sure the first 4 are the defaults as per user request.
      if (populatedData.overallProgress.length < 4) {
        // Fallback to absolute defaults if logic above is too complex
        populatedData.overallProgress = DEFAULT_CATEGORIES.map((cat, idx) => {
          const existing = backendItems[idx];
          return {
            category: cat,
            percentage: existing ? existing.percentage : "",
          };
        });
      }
    } else {
      populatedData.overallProgress = backendItems;
    }

    // Map plannedVsAchieved
    if (mpr.plannedVsAchieved && mpr.plannedVsAchieved.length > 0) {
      populatedData.plannedVsAchieved = mpr.plannedVsAchieved.map((item) => ({
        activity: item.activity || "",
        plannedDate: item.plannedDate
          ? format(parseISO(item.plannedDate), "yyyy-MM-dd")
          : "",
        actualDate: item.actualDate
          ? format(parseISO(item.actualDate), "yyyy-MM-dd")
          : "",
        variance: item.variance || "",
        remarkStatus: item.remarks || item.remarkStatus || "",
      }));
    }

    // Map manpowerSummaries to manpowerSummary
    // Use existing MPR manpower data as-is, since fetchAverageManpower will update it with API data
    if (mpr.manpowerSummaries && mpr.manpowerSummaries.length > 0) {
      populatedData.manpowerSummary = mpr.manpowerSummaries.map((item) => ({
        category: item.category || "",
        averageManpower: item.averageManpower || 0,
        peakManpower: item.peakManpower || 0,
        remarks: item.remarks || "",
      }));
    }

    // Map other sections
    if (mpr.plantMachinery && mpr.plantMachinery.length > 0) {
      populatedData.plantMachinery = mpr.plantMachinery.map((item) => ({
        equipmentDetails: item.equipmentName || "",
        capacity: item.capacity || "",
        quantity: item.quantity || 0,
        utilizationEfficiency: item.utilizationEfficiency || "",
        remarks: item.remarks || "",
      }));
    }

    // Map qualityAssurance
    if (mpr.qualityAssurance) {
      populatedData.qualityControl = {
        materialTestingSummary: (
          mpr.qualityAssurance.materialTestingSummary || []
        ).map((item) => ({
          ...item,
          testConductedDate: item.testConductedDate
            ? format(parseISO(item.testConductedDate), "yyyy-MM-dd")
            : "",
        })),
        inspectionAndNCRSummary: (
          mpr.qualityAssurance.inspectionAndNCRSummary || []
        ).map((item) => ({
          ...item,
          date: item.date ? format(parseISO(item.date), "yyyy-MM-dd") : "",
        })),
      };
    }

    // Map safetyHSE
    if (mpr.safetyHSE && mpr.safetyHSE.length > 0) {
      populatedData.safetyHSE = mpr.safetyHSE.map((item) => ({
        _id: item._id,
        date: item.date ? format(parseISO(item.date), "yyyy-MM-dd") : "",
        parameter: item.parameter || "",
        description: item.description || "",
        status: item.status || "Open",
        remarks: item.remarks || "",
      }));
    }

    // Map materialManagement
    if (mpr.materialManagement && mpr.materialManagement.length > 0) {
      populatedData.materialManagement = mpr.materialManagement.map((item) => ({
        materialName: item.materialName || "",
        received: item.received || 0,
        consumed: item.consumed || 0,
        remarks: item.remarks || "",
      }));
    }

    // Map drawingsApprovals - Handle new structure with GFC and Pending drawings
    populatedData.drawingsApprovals = {
      gfcDrawingReceived: mpr.drawingsApprovals?.gfcDrawingReceived || [],
      pendingDrawing: mpr.drawingsApprovals?.pendingDrawing || [],
    };

    // Map keyIssues
    if (mpr.keyIssues && mpr.keyIssues.length > 0) {
      populatedData.keyIssues = mpr.keyIssues.map((item) => ({
        issueDescription: item.issueDescription || "",
        delayReasons: item.delayReasons || "",
        mitigationMeasures: item.mitigationMeasures || "",
        impact: item.impact || "",
        remarks: item.remarks || "",
      }));
    }

    // Map clientDecisions to decisionsRequired
    if (mpr.clientDecisions && mpr.clientDecisions.length > 0) {
      populatedData.decisionsRequired = mpr.clientDecisions.map((item) => {
        return {
          _id: item._id,
          decisionTopic:
            item.decisionTopic ||
            (item.approvals || item.designClarifications
              ? `${item.approvals || ""} | ${item.designClarifications || ""}`
              : ""),
          requiredByDate: item.requiredByDate
            ? format(parseISO(item.requiredByDate), "yyyy-MM-dd")
            : "",
          responsibility: item.responsibility || "",
          remarks: item.remarks || "",
        };
      });
    }

    // Map rfiLogs
    if (mpr.rfiLogs && mpr.rfiLogs.length > 0) {
      populatedData.rfiLogs = mpr.rfiLogs.map((item) => ({
        _id: item._id,
        rfiNo: item.rfiNo || "",
        description: item.description || "",
        issueDate: item.issueDate
          ? format(parseISO(item.issueDate), "yyyy-MM-dd")
          : "",
        status: item.status || "Open",
        expectedDate: item.expectedDate
          ? format(parseISO(item.expectedDate), "yyyy-MM-dd")
          : "",
        remarks: item.remark || item.remarks || "",
      }));
    }

    // Map financialStatus
    if (mpr.financialStatus && mpr.financialStatus.length > 0) {
      populatedData.financialStatus = mpr.financialStatus.map((item) => ({
        _id: item._id,
        description: item.description || "",
        amountSubmitted: (item.amountSubmitted || 0).toString(),
        amountCertified: (item.amountCertified || 0).toString(),
        paymentReceived: (item.paymentReceived || 0).toString(),
        remarks: item.remarks || "",
      }));
    }

    // Map lookAheadPlan
    if (mpr.lookAheadPlan && mpr.lookAheadPlan.length > 0) {
      populatedData.lookAheadPlan = mpr.lookAheadPlan.map((item) => ({
        activity: item.activity || "",
        startDate: item.startDate
          ? format(parseISO(item.startDate), "yyyy-MM-dd")
          : "",
        endDate: item.endDate
          ? format(parseISO(item.endDate), "yyyy-MM-dd")
          : "",
        resourceRequirement: item.resourceRequirement || "",
        remarks: item.remarks || "",
      }));
    }

    // Map photographicDocumentation to photos
    if (
      mpr.photographicDocumentation &&
      mpr.photographicDocumentation.length > 0
    ) {
      const photos = mpr.photographicDocumentation.map((photo) => {
        let shapes = [];
        try {
          shapes = photo.remarks ? JSON.parse(photo.remarks).shapes || [] : [];
        } catch (e) {
          console.error("Error parsing photo shapes:", e);
        }

        return {
          photoPath: photo.photoPath,
          description: photo.description || photo.photoTitle,
          shapes: shapes,
          uploadedAt: photo.dateTaken,
          name: photo.photoTitle || "Photo",
          imageUrl: `${url}/${photo.photoPath.replace(/\\/g, "/")}`,
        };
      });
      setUploadedPhotos(photos);
    }

    // Map reports
    if (mpr.reports && mpr.reports.length > 0) {
      const reports = mpr.reports.map((report) => ({
        name: report.reportTitle,
        reportTitle: report.reportTitle,
        filePath: report.filePath,
        uploadDate: report.uploadDate,
        remarks: report.remarks,
      }));
      setUploadedReports(reports);
    }

    // Set all data at once to avoid multiple re-renders
    setMprData(populatedData);
  };

  const handleAddExecutiveSummary = () => {
    if (
      newExecutiveSummary.overallStatus ||
      newExecutiveSummary.keyAchievements ||
      newExecutiveSummary.majorConcerns
    ) {
      setMprData((prev) => ({
        ...prev,
        executiveSummary: { ...newExecutiveSummary },
      }));
      if (clearNewExecutiveSummary) clearNewExecutiveSummary();
      setNewExecutiveSummary({
        overallStatus: "",
        keyAchievements: "",
        majorConcerns: "",
      });
      setShowExecutiveSummaryForm(false);
    }
  };

  // Calculate overall progress total
  useEffect(() => {
    const total = (mprData.overallProgress || []).reduce((sum, item) => {
      // Clean percentage string and convert to number
      const val =
        typeof item.percentage === "string"
          ? item.percentage.replace("%", "")
          : item.percentage;
      return sum + (parseFloat(val) || 0);
    }, 0);
    setOverallProgressTotal(total);

    // Auto-switch to view mode if total is 100
    if (total === 100 && isOverallProgressEditMode) {
      // Optional: auto-switch logic could go here, but might be jarring
    }
  }, [mprData.overallProgress, isOverallProgressEditMode]);

  const handleOverallProgressChange = (index, field, value) => {
    const updatedProgress = [...mprData.overallProgress];
    updatedProgress[index] = { ...updatedProgress[index], [field]: value };
    setMprData((prev) => ({ ...prev, overallProgress: updatedProgress }));
  };

  const addOverallProgressRow = () => {
    setMprData((prev) => ({
      ...prev,
      overallProgress: [
        ...prev.overallProgress,
        { category: "", percentage: "0" },
      ],
    }));
  };

  const removeOverallProgressRow = (index) => {
    const updatedProgress = mprData.overallProgress.filter(
      (_, i) => i !== index,
    );
    setMprData((prev) => ({ ...prev, overallProgress: updatedProgress }));
  };

  const handleAddManpower = () => {
    if (
      newManpower.category &&
      (newManpower.averageManpower ||
        newManpower.peakManpower ||
        newManpower.remarks)
    ) {
      const entry = {
        category: newManpower.category,
        averageManpower: parseFloat(newManpower.averageManpower) || 0,
        peakManpower: parseFloat(newManpower.peakManpower) || 0,
        remarks: newManpower.remarks || "",
      };
      setMprData((prev) => ({
        ...prev,
        manpowerSummary: [...prev.manpowerSummary, entry],
      }));
      if (clearNewManpower) clearNewManpower();
      setNewManpower({
        category: "",
        averageManpower: "",
        peakManpower: "",
        remarks: "",
      });
      setShowManpowerForm(false);
    }
  };

  const handleAddPlantMachinery = () => {
    if (
      newPlantMachinery.equipmentDetails ||
      newPlantMachinery.capacity ||
      newPlantMachinery.quantity ||
      newPlantMachinery.utilizationEfficiency ||
      newPlantMachinery.remarks
    ) {
      const entry = {
        equipmentDetails: newPlantMachinery.equipmentDetails,
        capacity: newPlantMachinery.capacity,
        quantity: parseInt(newPlantMachinery.quantity) || 0,
        utilizationEfficiency: newPlantMachinery.utilizationEfficiency,
        remarks: newPlantMachinery.remarks,
      };
      setMprData((prev) => ({
        ...prev,
        plantMachinery: [...prev.plantMachinery, entry],
      }));
      if (clearNewPlantMachinery) clearNewPlantMachinery();
      setNewPlantMachinery({
        equipmentDetails: "",
        capacity: "",
        quantity: "",
        utilizationEfficiency: "",
        remarks: "",
      });
      setShowPlantMachineryForm(false);
    }
  };

  const handleAddMaterial = () => {
    if (
      newMaterial.materialName ||
      newMaterial.received ||
      newMaterial.consumed ||
      newMaterial.remarks
    ) {
      const entry = {
        materialName: newMaterial.materialName,
        received: parseFloat(newMaterial.received) || 0,
        consumed: parseFloat(newMaterial.consumed) || 0,
        remarks: newMaterial.remarks,
      };
      setMprData((prev) => ({
        ...prev,
        materialManagement: [...prev.materialManagement, entry],
      }));
      if (clearNewMaterial) clearNewMaterial();
      setNewMaterial({
        materialName: "",
        received: "",
        consumed: "",
        remarks: "",
      });
      setShowMaterialForm(false);
    }
  };

  const handleAddMaterialTest = () => {
    if (newMaterialTest.testDescription || newMaterialTest.reportId) {
      const entry = { ...newMaterialTest };
      setMprData((prev) => ({
        ...prev,
        qualityControl: {
          ...prev.qualityControl,
          materialTestingSummary: [
            ...prev.qualityControl.materialTestingSummary,
            entry,
          ],
        },
      }));
      if (clearNewMaterialTest) clearNewMaterialTest();
      setNewMaterialTest({
        testDescription: "",
        testConductedDate: "",
        reportId: "",
        result: "",
        status: "",
        remarks: "",
      });
      setShowMaterialTestForm(false);
    }
  };

  const handleAddInspectionNCR = () => {
    if (newInspectionNCR.ncrNo || newInspectionNCR.description) {
      const entry = { ...newInspectionNCR };
      setMprData((prev) => ({
        ...prev,
        qualityControl: {
          ...prev.qualityControl,
          inspectionAndNCRSummary: [
            ...prev.qualityControl.inspectionAndNCRSummary,
            entry,
          ],
        },
      }));
      if (clearNewInspectionNCR) clearNewInspectionNCR();
      setNewInspectionNCR({
        ncrNo: "",
        date: "",
        category: "",
        description: "",
        status: "",
      });
      setShowInspectionNCRForm(false);
    }
  };
  const handleAddSafety = () => {
    if (
      newSafety.parameter ||
      newSafety.description ||
      newSafety.remarks ||
      newSafety.date
    ) {
      const entry = { ...newSafety };
      setMprData((prev) => ({
        ...prev,
        safetyHSE: [...prev.safetyHSE, entry],
      }));
      if (clearNewSafety) clearNewSafety();
      setNewSafety({
        date: "",
        parameter: "",
        description: "",
        status: "Open",
        remarks: "",
      });
      setShowSafetyForm(false);
    } else {
      showToast(
        "warning",
        "Empty Entry",
        "Please fill at least one field before adding.",
      );
    }
  };

  const handleAddFinancial = () => {
    if (
      newFinancial.description ||
      newFinancial.amountSubmitted ||
      newFinancial.remarks
    ) {
      const entry = { ...newFinancial };
      setMprData((prev) => ({
        ...prev,
        financialStatus: [...prev.financialStatus, entry],
      }));
      if (clearNewFinancial) clearNewFinancial();
      setNewFinancial({
        description: "",
        amountSubmitted: "",
        amountCertified: "",
        paymentReceived: "",
        remarks: "",
      });
      setShowFinancialForm(false);
    }
  };

  const handleAddDrawings = () => {
    // Check if either GFC or Pending drawing data is provided
    const hasGFCData =
      newDrawings.gfcCategory ||
      newDrawings.gfcDrawingType ||
      newDrawings.gfcDrawingNo ||
      newDrawings.gfcRevision ||
      newDrawings.gfcReceivedDate ||
      newDrawings.gfcRemarkStatus;
    const hasPendingData =
      newDrawings.pendingCategory ||
      newDrawings.pendingDrawingType ||
      newDrawings.pendingExpectedDate ||
      newDrawings.pendingCurrentStatus ||
      newDrawings.pendingRemark;

    if (hasGFCData || hasPendingData) {
      const updatedDrawings = {
        gfcDrawingReceived: hasGFCData
          ? [
              ...(mprData.drawingsApprovals?.gfcDrawingReceived || []),
              {
                category: newDrawings.gfcCategory,
                drawingType: newDrawings.gfcDrawingType,
                drawingNo: newDrawings.gfcDrawingNo,
                revision: newDrawings.gfcRevision,
                receivedDate: newDrawings.gfcReceivedDate,
                remark: newDrawings.gfcRemarkStatus,
                status: newDrawings.gfcRemarkStatus,
              },
            ]
          : mprData.drawingsApprovals?.gfcDrawingReceived || [],
        pendingDrawing: hasPendingData
          ? [
              ...(mprData.drawingsApprovals?.pendingDrawing || []),
              {
                category: newDrawings.pendingCategory,
                drawingType: newDrawings.pendingDrawingType,
                expectedDate: newDrawings.pendingExpectedDate,
                currentStatus: newDrawings.pendingCurrentStatus,
                remark: newDrawings.pendingRemark,
              },
            ]
          : mprData.drawingsApprovals?.pendingDrawing || [],
      };

      setMprData((prev) => ({
        ...prev,
        drawingsApprovals: updatedDrawings,
      }));
      if (clearNewDrawings) clearNewDrawings();
      setNewDrawings({
        gfcCategory: "",
        gfcDrawingType: "",
        gfcDrawingNo: "",
        gfcRevision: "",
        gfcReceivedDate: "",
        gfcRemarkStatus: "",
        pendingCategory: "",
        pendingDrawingType: "",
        pendingExpectedDate: "",
        pendingCurrentStatus: "",
        pendingRemark: "",
      });
      setShowDrawingsForm(false);
      showToast("success", "Entry Added", "Drawing entry added successfully!");
    }
  };

  const handleAddIssues = () => {
    if (newIssues.issueDescription || newIssues.mitigationMeasures) {
      const entry = { ...newIssues };
      setMprData((prev) => ({
        ...prev,
        keyIssues: [...prev.keyIssues, entry],
      }));
      if (clearNewIssues) clearNewIssues();
      setNewIssues({
        issueDescription: "",
        delayReasons: "",
        mitigationMeasures: "",
        impact: "",
        remarks: "",
      });
      setShowIssuesForm(false);
    }
  };

  const handleAddDecisions = () => {
    if (newDecisions.decisionTopic || newDecisions.responsibility) {
      const finalResponsibility =
        newDecisions.responsibility === "Other"
          ? newDecisions.otherResponsibility
          : newDecisions.responsibility;

      const entry = {
        ...newDecisions,
        responsibility: finalResponsibility,
      };

      setMprData((prev) => ({
        ...prev,
        decisionsRequired: [...prev.decisionsRequired, entry],
      }));
      if (clearNewDecisions) clearNewDecisions();
      setNewDecisions({
        decisionTopic: "",
        requiredByDate: "",
        responsibility: "",
        otherResponsibility: "",
        remarks: "",
      });
      setShowDecisionsForm(false);
      showToast(
        "success",
        "Entry Added",
        "Decision required entry added successfully!",
      );
    } else {
      showToast(
        "warning",
        "Empty Entry",
        "Please fill at least Decision Topic or Responsibility.",
      );
    }
  };

  const handleAddRFI = () => {
    if (!newRFI.rfiNo || !newRFI.description || !newRFI.issueDate) {
      showToast(
        "warning",
        "Missing Fields",
        "Please fill RFI No, Description and Issue Date.",
      );
      return;
    }

    if (newRFI.status === "Open" && !newRFI.expectedDate) {
      showToast(
        "warning",
        "Missing Expected Date",
        "Expected Date is compulsory for Open status.",
      );
      return;
    }

    const entry = { ...newRFI };
    setMprData((prev) => ({
      ...prev,
      rfiLogs: [...(prev.rfiLogs || []), entry],
    }));
    if (clearNewRFI) clearNewRFI();
    setNewRFI({
      rfiNo: "",
      description: "",
      issueDate: "",
      status: "Open",
      expectedDate: "",
      remarks: "",
    });
    setShowRFIForm(false);
    showToast("success", "Entry Added", "RFI entry added successfully!");
  };

  const handleAddLookAhead = () => {
    if (newLookAhead.activity || newLookAhead.resourceRequirement) {
      const entry = { ...newLookAhead };
      setMprData((prev) => ({
        ...prev,
        lookAheadPlan: [...prev.lookAheadPlan, entry],
      }));
      if (clearNewLookAhead) clearNewLookAhead();
      setNewLookAhead({
        activity: "",
        startDate: "",
        endDate: "",
        resourceRequirement: "",
        remarks: "",
      });
      setShowLookAheadForm(false);
      showToast(
        "success",
        "Entry Added",
        "Look ahead entry added successfully!",
      );
    } else {
      showToast(
        "warning",
        "Empty Entry",
        "Please fill Activity or Resource Requirement.",
      );
    }
  };

  const handleAddPlannedVsAchieved = () => {
    const entry = {
      activity: newPlannedVsAchieved.activity || "",
      plannedDate: newPlannedVsAchieved.plannedDate || "",
      actualDate: newPlannedVsAchieved.actualDate || "",
      variance: newPlannedVsAchieved.variance || "",
      remarkStatus: newPlannedVsAchieved.remarkStatus || "",
    };

    setMprData((prev) => ({
      ...prev,
      plannedVsAchieved: [...prev.plannedVsAchieved, entry],
    }));
    if (clearNewPlannedVsAchieved) clearNewPlannedVsAchieved();
    setNewPlannedVsAchieved({
      activity: "",
      plannedDate: "",
      actualDate: "",
      variance: "",
      remarkStatus: "",
    });
    setShowPlannedVsAchievedForm(false);
  };

  // Handle deleting entries
  const handleDeleteEntry = async (section, index) => {
    try {
      let sectionEntry;
      if (
        section === "materialTestingSummary" ||
        section === "inspectionAndNCRSummary"
      ) {
        sectionEntry = mprData.qualityControl[section][index];
      } else if (
        section === "gfcDrawingReceived" ||
        section === "pendingDrawing"
      ) {
        sectionEntry = mprData.drawingsApprovals[section][index];
      } else {
        sectionEntry = mprData[section][index];
      }

      // Track deleted category to prevent API from adding it back
      if (section === "manpowerSummary" && sectionEntry.category) {
        setDeletedCategories((prev) => [
          ...prev,
          sectionEntry.category.toLowerCase(),
        ]);
      }

      // Always remove from local state first
      if (
        section === "materialTestingSummary" ||
        section === "inspectionAndNCRSummary"
      ) {
        setMprData((prev) => ({
          ...prev,
          qualityControl: {
            ...prev.qualityControl,
            [section]: prev.qualityControl[section].filter(
              (_, i) => i !== index,
            ),
          },
        }));
      } else if (
        section === "gfcDrawingReceived" ||
        section === "pendingDrawing"
      ) {
        setMprData((prev) => ({
          ...prev,
          drawingsApprovals: {
            ...prev.drawingsApprovals,
            [section]: prev.drawingsApprovals[section].filter(
              (_, i) => i !== index,
            ),
          },
        }));
      } else {
        setMprData((prev) => ({
          ...prev,
          [section]: prev[section].filter((_, i) => i !== index),
        }));
      }

      // If entry has an _id, try to delete it from the database
      if (sectionEntry && sectionEntry._id) {
        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };

        // First, try to find if there's an existing MPR for this month
        const monthStartStr = format(monthStartDate, "yyyy-MM-dd");
        const monthEndStr = format(monthEndDate, "yyyy-MM-dd");

        try {
          const response = await axios.get(
            `${url}/api/mpr/fetch-by-project/${projectId}?monthStartDate=${monthStartStr}&monthEndDate=${monthEndStr}`,
            config,
          );

          if (response.data.mprs && response.data.mprs.length > 0) {
            const mpr = response.data.mprs[0];
            const backendSectionName = getBackendSectionName(section);

            try {
              // Delete from backend
              const deleteResponse = await axios.post(
                `${url}/api/mpr/delete-entry/${mpr._id}`,
                {
                  sectionName: backendSectionName,
                  entryId: sectionEntry._id,
                },
                {
                  headers: {
                    Authorization: token ? `Bearer ${token}` : undefined,
                  },
                },
              );

              showToast(
                "success",
                "Entry Deleted",
                "Entry deleted successfully from database.",
              );
            } catch (deleteError) {
              console.error("Backend delete failed:", deleteError);
              console.error(
                "Delete error response:",
                deleteError.response?.data,
              );

              // If backend delete fails, we've already removed it from local state
              // The next save operation will update the database without this entry
              showToast(
                "warning",
                "Local Delete Only",
                "Entry removed from form. Will be updated in database when you save.",
              );
            }
          } else {
            console.log("No MPR found for this month");
            showToast(
              "warning",
              "No MPR Found",
              "No existing MPR found to delete from database.",
            );
          }
        } catch (fetchError) {
          console.error("Failed to fetch MPR for deletion:", fetchError);
          showToast(
            "warning",
            "Fetch Failed",
            "Could not verify MPR for deletion. Entry removed from form.",
          );
        }
      } else {
        console.log("No _id found, removing local entry only");
        showToast(
          "info",
          "Local Entry",
          "Removing local entry (not saved to database yet).",
        );
      }
    } catch (err) {
      console.error("Error in delete process:", err);
      showToast(
        "error",
        "Delete Failed",
        "Failed to delete entry. Please try again.",
      );
    }
  };

  // Handle editing entries
  const handleEditEntry = (section, index) => {
    let entry;
    if (
      section === "materialTestingSummary" ||
      section === "inspectionAndNCRSummary"
    ) {
      entry = mprData.qualityControl[section][index];
    } else if (
      section === "gfcDrawingReceived" ||
      section === "pendingDrawing"
    ) {
      entry = mprData.drawingsApprovals[section][index];
    } else {
      entry = mprData[section][index];
    }

    // Open appropriate form and populate with existing data
    switch (section) {
      case "overallProgress":
        setShowOverallProgressForm(false); // Close add form if open
        setNewOverallProgress({
          structural: entry.structural || "",
          mep: entry.mep || "",
          finishing: entry.finishing || "",
          externalDevelopment: entry.externalDevelopment || "",
        });
        setShowOverallProgressForm(true);
        setEditingEntry({ section, index, data: entry });
        break;
      case "plannedVsAchieved":
        setNewPlannedVsAchieved({
          activity: entry.activity || "",
          plannedDate: entry.plannedDate || "",
          actualDate: entry.actualDate || "",
          variance: entry.variance || "",
          remarkStatus: entry.remarkStatus || "",
        });
        // Use inline editing - show form below the specific entry
        setInlineEditingIndex(index);
        setEditingEntry({ section, index, data: entry });
        break;
      case "manpowerSummary":
        setShowManpowerForm(false); // Close add form if open
        setNewManpower({
          category: entry.category || "",
          averageManpower: entry.averageManpower?.toString() || "",
          peakManpower: entry.peakManpower?.toString() || "",
          remarks: entry.remarks || "",
        });
        // Use inline editing - show form below the specific entry
        setInlineEditingIndex(index);
        setEditingEntry({ section, index, data: entry });
        break;
      case "materialManagement":
        setShowMaterialForm(false); // Close add form if open
        setNewMaterial({
          materialName: entry.materialName || "",
          received: entry.received?.toString() || "",
          consumed: entry.consumed?.toString() || "",
          remarks: entry.remarks || "",
        });
        setEditingEntry({ section, index, data: entry });
        setShowMaterialForm(true);
        break;
      case "keyIssues":
        setNewIssues({
          issueDescription: entry.issueDescription || "",
          delayReasons: entry.delayReasons || "",
          mitigationMeasures: entry.mitigationMeasures || "",
          impact: entry.impact || "",
          remarks: entry.remarks || "",
        });
        setEditingEntry({ section, index, data: entry });
        setShowIssuesForm(true);
        break;
      case "materialTestingSummary":
        setNewMaterialTest({
          testDescription: entry.testDescription || "",
          testConductedDate: entry.testConductedDate || "",
          reportId: entry.reportId || "",
          result: entry.result || "",
          status: entry.status || "",
          remarks: entry.remarks || "",
        });
        setEditingEntry({ section, index, data: entry });
        setShowMaterialTestForm(true);
        break;
      case "inspectionAndNCRSummary":
        setNewInspectionNCR({
          ncrNo: entry.ncrNo || "",
          date: entry.date || "",
          category: entry.category || "",
          description: entry.description || "",
          status: entry.status || "",
        });
        setEditingEntry({ section, index, data: entry });
        setShowInspectionNCRForm(true);
        break;
      case "safetyHSE":
        setNewSafety({
          date: entry.date || "",
          parameter: entry.parameter || "",
          description: entry.description || "",
          status: entry.status || "Open",
          remarks: entry.remarks || "",
        });
        setEditingEntry({ section, index, data: entry });
        setShowSafetyForm(true);
        break;
      case "gfcDrawingReceived":
        setNewDrawings({
          gfcCategory: entry.category || "",
          gfcDrawingType: entry.drawingType || "",
          gfcDrawingNo: entry.drawingNo || "",
          gfcRevision: entry.revision || "",
          gfcReceivedDate: entry.receivedDate
            ? new Date(entry.receivedDate).toISOString().split("T")[0]
            : "",
          gfcRemarkStatus: entry.remark || entry.status || "",
          pendingCategory: "",
          pendingDrawingType: "",
          pendingExpectedDate: "",
          pendingCurrentStatus: "",
          pendingRemark: "",
        });
        setEditingEntry({ section, index, data: entry });
        setShowDrawingsForm(true);
        break;
      case "pendingDrawing":
        setNewDrawings({
          gfcCategory: "",
          gfcDrawingType: "",
          gfcDrawingNo: "",
          gfcRevision: "",
          gfcReceivedDate: "",
          gfcRemarkStatus: "",
          pendingCategory: entry.category || "",
          pendingDrawingType: entry.drawingType || "",
          pendingExpectedDate: entry.expectedDate
            ? new Date(entry.expectedDate).toISOString().split("T")[0]
            : "",
          pendingCurrentStatus: entry.currentStatus || "",
          pendingRemark: entry.remark || "",
        });
        setEditingEntry({ section, index, data: entry });
        setShowDrawingsForm(true);
        break;
      case "financialStatus":
        setNewFinancial({
          description: entry.description || "",
          amountSubmitted: entry.amountSubmitted?.toString() || "",
          amountCertified: entry.amountCertified?.toString() || "",
          paymentReceived: entry.paymentReceived?.toString() || "",
          remarks: entry.remarks || "",
        });
        setEditingEntry({ section, index, data: entry });
        setShowFinancialForm(true);
        break;
      case "rfiLogs":
        setNewRFI({
          rfiNo: entry.rfiNo || "",
          description: entry.description || "",
          issueDate: entry.issueDate
            ? new Date(entry.issueDate).toISOString().split("T")[0]
            : "",
          status: entry.status || "Open",
          expectedDate: entry.expectedDate
            ? new Date(entry.expectedDate).toISOString().split("T")[0]
            : "",
          remarks: entry.remarks || entry.remark || "",
        });
        setEditingEntry({ section, index, data: entry });
        setShowRFIForm(true);
        break;

      case "lookAheadPlan":
        setNewLookAhead({
          activity: entry.activity || "",
          startDate: entry.startDate
            ? new Date(entry.startDate).toISOString().split("T")[0]
            : "",
          endDate: entry.endDate
            ? new Date(entry.endDate).toISOString().split("T")[0]
            : "",
          resourceRequirement: entry.resourceRequirement || "",
          remarks: entry.remarks || "",
        });
        setEditingEntry({ section, index, data: entry });
        setShowLookAheadForm(true);
        break;

      case "plantMachinery":
        setShowPlantMachineryForm(false); // Close add form if open
        setNewPlantMachinery({
          equipmentDetails: entry.equipmentDetails || "",
          capacity: entry.capacity || "",
          quantity: entry.quantity || "",
          utilizationEfficiency: entry.utilizationEfficiency || "",
          remarks: entry.remarks || "",
        });
        setEditingEntry({ section, index, data: entry });
        // plantMachinery uses inline editing mostly
        break;
      case "decisionsRequired":
        setNewDecisions({
          decisionTopic:
            entry.decisionTopic ||
            (entry.approvals || entry.designClarifications
              ? `${entry.approvals || ""} | ${entry.designClarifications || ""}`
              : ""),
          requiredByDate: entry.requiredByDate
            ? new Date(entry.requiredByDate).toISOString().split("T")[0]
            : "",
          responsibility: responsibilityOptions.includes(entry.responsibility)
            ? entry.responsibility
            : entry.responsibility
              ? "Other"
              : "",
          otherResponsibility: responsibilityOptions.includes(
            entry.responsibility,
          )
            ? ""
            : entry.responsibility || "",
          remarks: entry.remarks || "",
        });
        setEditingEntry({ section, index, data: entry });
        setShowDecisionsForm(true);
        break;
      default:
        break;
    }
  };

  // Handle updating entries
  const handleUpdateEntry = () => {
    if (!editingEntry.section || editingEntry.index === null) return;

    const { section, index } = editingEntry;

    switch (section) {
      case "overallProgress":
        // Validate numeric values
        const structuralValue = parseFloat(newOverallProgress.structural) || 0;
        const mepValue = parseFloat(newOverallProgress.mep) || 0;
        const finishingValue = parseFloat(newOverallProgress.finishing) || 0;
        const externalValue =
          parseFloat(newOverallProgress.externalDevelopment) || 0;

        // Ensure values are between 0 and 100
        const validatedStructural = Math.min(Math.max(structuralValue, 0), 100);
        const validatedMep = Math.min(Math.max(mepValue, 0), 100);
        const validatedFinishing = Math.min(Math.max(finishingValue, 0), 100);
        const validatedExternal = Math.min(Math.max(externalValue, 0), 100);

        const updatedEntry = {
          ...mprData.overallProgress[index], // Preserve all existing fields
          structuralProgress: validatedStructural,
          mepProgress: validatedMep,
          finishingProgress: validatedFinishing,
          externalDevelopmentProgress: validatedExternal,
          // Also keep the frontend field names for display
          structural: validatedStructural.toString(),
          mep: validatedMep.toString(),
          finishing: validatedFinishing.toString(),
          externalDevelopment: validatedExternal.toString(),
        };

        setMprData((prev) => ({
          ...prev,
          overallProgress: prev.overallProgress.map((item, i) =>
            i === index ? updatedEntry : item,
          ),
        }));
        if (clearNewOverallProgress) clearNewOverallProgress();
        setNewOverallProgress({ category: "", percentage: "" });
        break;

      case "plannedVsAchieved":
        const updatedPlannedEntry = {
          activity: newPlannedVsAchieved.activity || "",
          plannedDate: newPlannedVsAchieved.plannedDate || "",
          actualDate: newPlannedVsAchieved.actualDate || "",
          variance: newPlannedVsAchieved.variance || "",
          remarkStatus: newPlannedVsAchieved.remarkStatus || "",
        };

        setMprData((prev) => ({
          ...prev,
          plannedVsAchieved: prev.plannedVsAchieved.map((item, i) =>
            i === index ? updatedPlannedEntry : item,
          ),
        }));
        if (clearNewPlannedVsAchieved) clearNewPlannedVsAchieved();
        setNewPlannedVsAchieved({
          activity: "",
          plannedDate: "",
          actualDate: "",
          variance: "",
          remarkStatus: "",
        });
        break;

      case "manpowerSummary":
        setMprData((prev) => ({
          ...prev,
          manpowerSummary: prev.manpowerSummary.map((item, i) =>
            i === index
              ? {
                  ...item,
                  category: newManpower.category,
                  averageManpower: parseFloat(newManpower.averageManpower) || 0,
                  peakManpower: parseFloat(newManpower.peakManpower) || 0,
                  remarks: newManpower.remarks || "",
                }
              : item,
          ),
        }));
        if (clearNewManpower) clearNewManpower();
        setNewManpower({
          category: "",
          averageManpower: "",
          peakManpower: "",
          remarks: "",
        });
        break;

      case "materialManagement":
        setMprData((prev) => ({
          ...prev,
          materialManagement: prev.materialManagement.map((item, i) =>
            i === index
              ? {
                  ...item,
                  materialName: newMaterial.materialName,
                  received: parseFloat(newMaterial.received) || 0,
                  consumed: parseFloat(newMaterial.consumed) || 0,
                  remarks: newMaterial.remarks || "",
                }
              : item,
          ),
        }));
        if (clearNewMaterial) clearNewMaterial();
        setNewMaterial({
          materialName: "",
          received: "",
          consumed: "",
          remarks: "",
        });
        break;

      case "plantMachinery":
        const updatedMachineryEntry = {
          ...mprData.plantMachinery[index],
          equipmentDetails: newPlantMachinery.equipmentDetails,
          capacity: newPlantMachinery.capacity,
          quantity: parseInt(newPlantMachinery.quantity) || 0,
          utilizationEfficiency: newPlantMachinery.utilizationEfficiency,
          remarks: newPlantMachinery.remarks,
        };
        setMprData((prev) => ({
          ...prev,
          plantMachinery: prev.plantMachinery.map((item, i) =>
            i === index ? updatedMachineryEntry : item,
          ),
        }));
        if (clearNewPlantMachinery) clearNewPlantMachinery();
        setNewPlantMachinery({
          equipmentDetails: "",
          capacity: "",
          quantity: "",
          utilizationEfficiency: "",
          remarks: "",
        });
        setShowPlantMachineryForm(false);
        break;

      case "materialTestingSummary":
        setMprData((prev) => ({
          ...prev,
          qualityControl: {
            ...prev.qualityControl,
            materialTestingSummary:
              prev.qualityControl.materialTestingSummary.map((item, i) =>
                i === index ? { ...item, ...newMaterialTest } : item,
              ),
          },
        }));
        if (clearNewMaterialTest) clearNewMaterialTest();
        setNewMaterialTest({
          testDescription: "",
          testConductedDate: "",
          reportId: "",
          result: "",
          status: "",
          remarks: "",
        });
        setShowMaterialTestForm(false);
        break;

      case "inspectionAndNCRSummary":
        setMprData((prev) => ({
          ...prev,
          qualityControl: {
            ...prev.qualityControl,
            inspectionAndNCRSummary:
              prev.qualityControl.inspectionAndNCRSummary.map((item, i) =>
                i === index ? { ...item, ...newInspectionNCR } : item,
              ),
          },
        }));
        if (clearNewInspectionNCR) clearNewInspectionNCR();
        setNewInspectionNCR({
          ncrNo: "",
          date: "",
          category: "",
          description: "",
          status: "",
        });
        setShowInspectionNCRForm(false);
        break;

      case "financialStatus":
        setMprData((prev) => ({
          ...prev,
          financialStatus: prev.financialStatus.map((item, i) =>
            i === index
              ? {
                  ...item,
                  description: newFinancial.description,
                  amountSubmitted: newFinancial.amountSubmitted,
                  amountCertified: newFinancial.amountCertified,
                  paymentReceived: newFinancial.paymentReceived,
                  remarks: newFinancial.remarks,
                }
              : item,
          ),
        }));
        if (clearNewFinancial) clearNewFinancial();
        setNewFinancial({
          description: "",
          amountSubmitted: "",
          amountCertified: "",
          paymentReceived: "",
          remarks: "",
        });
        setShowFinancialForm(false);
        break;

      case "safetyHSE":
        setMprData((prev) => ({
          ...prev,
          safetyHSE: prev.safetyHSE.map((item, i) =>
            i === index
              ? {
                  ...item,
                  date: newSafety.date,
                  parameter: newSafety.parameter,
                  description: newSafety.description,
                  status: newSafety.status,
                  remarks: newSafety.remarks,
                }
              : item,
          ),
        }));
        if (clearNewSafety) clearNewSafety();
        setNewSafety({
          date: "",
          parameter: "",
          description: "",
          status: "Open",
          remarks: "",
        });
        setShowSafetyForm(false);
        break;

      case "gfcDrawingReceived":
        setMprData((prev) => ({
          ...prev,
          drawingsApprovals: {
            ...prev.drawingsApprovals,
            gfcDrawingReceived: prev.drawingsApprovals.gfcDrawingReceived.map(
              (item, i) =>
                i === index
                  ? {
                      category: newDrawings.gfcCategory || item.category,
                      drawingType:
                        newDrawings.gfcDrawingType || item.drawingType,
                      drawingNo: newDrawings.gfcDrawingNo || item.drawingNo,
                      revision: newDrawings.gfcRevision || item.revision,
                      receivedDate:
                        newDrawings.gfcReceivedDate || item.receivedDate,
                      remark: newDrawings.gfcRemarkStatus || item.remark,
                      status: newDrawings.gfcRemarkStatus || item.status,
                    }
                  : item,
            ),
          },
        }));
        if (clearNewDrawings) clearNewDrawings();
        setNewDrawings({
          gfcCategory: "",
          gfcDrawingType: "",
          gfcDrawingNo: "",
          gfcRevision: "",
          gfcReceivedDate: "",
          gfcRemarkStatus: "",
          pendingCategory: "",
          pendingDrawingType: "",
          pendingExpectedDate: "",
          pendingCurrentStatus: "",
          pendingRemark: "",
        });
        setShowDrawingsForm(false);
        break;

      case "pendingDrawing":
        setMprData((prev) => ({
          ...prev,
          drawingsApprovals: {
            ...prev.drawingsApprovals,
            pendingDrawing: prev.drawingsApprovals.pendingDrawing.map(
              (item, i) =>
                i === index
                  ? {
                      category: newDrawings.pendingCategory || item.category,
                      drawingType:
                        newDrawings.pendingDrawingType || item.drawingType,
                      expectedDate:
                        newDrawings.pendingExpectedDate || item.expectedDate,
                      currentStatus:
                        newDrawings.pendingCurrentStatus || item.currentStatus,
                      remark: newDrawings.pendingRemark || item.remark,
                    }
                  : item,
            ),
          },
        }));
        if (clearNewDrawings) clearNewDrawings();
        setNewDrawings({
          gfcCategory: "",
          gfcDrawingType: "",
          gfcDrawingNo: "",
          gfcRevision: "",
          gfcReceivedDate: "",
          gfcRemarkStatus: "",
          pendingCategory: "",
          pendingDrawingType: "",
          pendingExpectedDate: "",
          pendingCurrentStatus: "",
          pendingRemark: "",
        });
        setShowDrawingsForm(false);
        break;

      case "keyIssues":
        setMprData((prev) => ({
          ...prev,
          keyIssues: prev.keyIssues.map((item, i) =>
            i === index ? { ...item, ...newIssues } : item,
          ),
        }));
        if (clearNewIssues) clearNewIssues();
        setNewIssues({
          issueDescription: "",
          delayReasons: "",
          mitigationMeasures: "",
          impact: "",
          remarks: "",
        });
        setShowIssuesForm(false);
        break;

      case "decisionsRequired":
        setMprData((prev) => ({
          ...prev,
          decisionsRequired: prev.decisionsRequired.map((item, i) =>
            i === index
              ? {
                  ...item,
                  decisionTopic: newDecisions.decisionTopic,
                  requiredByDate: newDecisions.requiredByDate,
                  responsibility:
                    newDecisions.responsibility === "Other"
                      ? newDecisions.otherResponsibility
                      : newDecisions.responsibility,
                  remarks: newDecisions.remarks,
                }
              : item,
          ),
        }));
        if (clearNewDecisions) clearNewDecisions();
        setNewDecisions({
          decisionTopic: "",
          requiredByDate: "",
          responsibility: "",
          otherResponsibility: "",
          remarks: "",
        });
        setShowDecisionsForm(false);
        break;

      case "rfiLogs":
        setMprData((prev) => ({
          ...prev,
          rfiLogs: prev.rfiLogs.map((item, i) =>
            i === index ? { ...newRFI } : item,
          ),
        }));
        if (clearNewRFI) clearNewRFI();
        setNewRFI({
          rfiNo: "",
          description: "",
          issueDate: "",
          status: "Open",
          expectedDate: "",
          remarks: "",
        });
        setShowRFIForm(false);
        break;

      case "lookAheadPlan":
        const updatedLookAheadEntry = {
          ...mprData.lookAheadPlan[index],
          activity: newLookAhead.activity || "",
          startDate: newLookAhead.startDate || "",
          endDate: newLookAhead.endDate || "",
          resourceRequirement: newLookAhead.resourceRequirement || "",
          remarks: newLookAhead.remarks || "",
        };

        setMprData((prev) => ({
          ...prev,
          lookAheadPlan: prev.lookAheadPlan.map((item, i) =>
            i === index ? updatedLookAheadEntry : item,
          ),
        }));
        if (clearNewLookAhead) clearNewLookAhead();
        setNewLookAhead({
          activity: "",
          startDate: "",
          endDate: "",
          resourceRequirement: "",
          remarks: "",
        });
        setShowLookAheadForm(false);
        break;

      default:
        break;
    }

    // Reset form and editing state
    if (section === "overallProgress") {
      if (clearNewOverallProgress) clearNewOverallProgress();
      setNewOverallProgress({
        category: "",
        percentage: "",
      });
      setShowOverallProgressForm(false);
    } else if (section === "plannedVsAchieved") {
      if (clearNewPlannedVsAchieved) clearNewPlannedVsAchieved();
      setNewPlannedVsAchieved({
        activity: "",
        plannedDate: "",
        actualDate: "",
        variance: "",
        remarkStatus: "",
      });
      setShowPlannedVsAchievedForm(false);
    } else if (section === "manpowerSummary") {
      if (clearNewManpower) clearNewManpower();
      setNewManpower({
        category: "",
        averageManpower: "",
        peakManpower: "",
        remarks: "",
      });
      setShowManpowerForm(false);
    } else if (section === "materialManagement") {
      if (clearNewMaterial) clearNewMaterial();
      setNewMaterial({
        materialName: "",
        received: "",
        consumed: "",
        remarks: "",
      });
      setShowMaterialForm(false);
    } else if (section === "safetyHSE") {
      if (clearNewSafety) clearNewSafety();
      setNewSafety({
        date: "",
        parameter: "",
        description: "",
        status: "Open",
        remarks: "",
      });
      setShowSafetyForm(false);
    } else if (section === "lookAheadPlan") {
      if (clearNewLookAhead) clearNewLookAhead();
      setNewLookAhead({
        activity: "",
        startDate: "",
        endDate: "",
        resourceRequirement: "",
        remarks: "",
      });
      setShowLookAheadForm(false);
    } else if (section === "materialTestingSummary") {
      if (clearNewMaterialTest) clearNewMaterialTest();
    } else if (section === "inspectionAndNCRSummary") {
      if (clearNewInspectionNCR) clearNewInspectionNCR();
    } else if (section === "financialStatus") {
      if (clearNewFinancial) clearNewFinancial();
    } else if (section === "keyIssues") {
      if (clearNewIssues) clearNewIssues();
    } else if (section === "decisionsRequired") {
      if (clearNewDecisions) clearNewDecisions();
    } else if (section === "gfcDrawingReceived" || section === "pendingDrawing") {
      if (clearNewDrawings) clearNewDrawings();
    } else if (section === "rfiLogs") {
      if (clearNewRFI) clearNewRFI();
    }

    setEditingEntry({ section: null, index: null, data: null });
    setInlineEditingIndex(null); // Reset inline editing
    showToast("success", "Entry Updated", "Entry updated successfully!");
  };

  // Handle inline editing updates for manpower
  const handleInlineUpdate = (index, field, value) => {
    setMprData((prev) => ({
      ...prev,
      manpowerSummary: prev.manpowerSummary.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  // Handle save inline edit
  const handleSaveInlineEdit = (index) => {
    if (editingEntry.section === "plantMachinery" && editingEntry.data) {
      setMprData((prev) => ({
        ...prev,
        plantMachinery: prev.plantMachinery.map((item, i) =>
          i === index ? { ...item, ...editingEntry.data } : item,
        ),
      }));
    }
    setInlineEditingIndex(null);
    setEditingEntry({ section: null, index: null, data: null });
    showToast("success", "Entry Updated", "Entry updated successfully!");
  };

  // Handle cancel inline edit
  const handleCancelInlineEdit = () => {
    setInlineEditingIndex(null);
    setEditingEntry({ section: null, index: null, data: null });
  };

  // Helper function to map frontend section names to backend section names
  const getBackendSectionName = (frontendSection) => {
    const sectionMap = {
      executiveSummary: "executiveSummaries",
      overallProgress: "overallProgress",
      plannedVsAchieved: "plannedVsAchieved",
      manpowerSummary: "manpowerSummaries",
      plantMachinery: "plantMachinery",
      materialManagement: "materialManagement",
      qualityControl: "qualityAssurance",
      safetyHSE: "safetyHSE",
      financialStatus: "financialStatus",
      drawingsApprovals: "drawingsApprovals",
      gfcDrawingReceived: "drawingsApprovals.gfcDrawingReceived",
      pendingDrawing: "drawingsApprovals.pendingDrawing",
      keyIssues: "keyIssues",
      decisionsRequired: "clientDecisions",
      lookAheadPlan: "lookAheadPlan",
    };
    return sectionMap[frontendSection] || frontendSection;
  };

  // Handle report upload
  const handleReportUpload = (event) => {
    const files = Array.from(event.target.files);
    setUploadedReports((prev) => [...prev, ...files]);
  };

  const handleRemoveReport = (index) => {
    setUploadedReports((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePhotoUploadWithDrawing = (photoData) => {
    const newPhoto = {
      file: photoData.file,
      title: photoData.title,
      shapes: photoData.shapes,
      imageUrl: photoData.imageUrl,
      name: photoData.file?.name || photoData.name,
      size: photoData.file?.size || photoData.size,
      type: photoData.file?.type || photoData.type,
    };

    const updatedPhotos = [...uploadedPhotos, newPhoto];
    setUploadedPhotos(updatedPhotos);
    
    // Update persistence
    setMprData({
      ...mprData,
      photos: updatedPhotos.map(p => ({
        title: p.title,
        shapes: p.shapes,
        imageUrl: p.imageUrl,
        name: p.name,
        size: p.size,
        type: p.type
      }))
    });

    setShowPhotoUploadDialog(false);
    showToast(
      "success",
      "Photo Added",
      "Photo with annotations added successfully!",
    );
  };

  const removePhoto = (index) => {
    const updatedPhotos = uploadedPhotos.filter((_, i) => i !== index);
    setUploadedPhotos(updatedPhotos);
    setMprData({
      ...mprData,
      photos: updatedPhotos.map(p => ({
        title: p.title,
        shapes: p.shapes,
        imageUrl: p.imageUrl,
        name: p.name,
        size: p.size,
        type: p.type
      }))
    });
  };

  // Save MPR
  const handleSaveMPR = async () => {
    try {
      setSaving(true);

      const formData = new FormData();

      // Add basic fields
      formData.append("projectId", projectId);
      formData.append("monthStartDate", format(monthStartDate, "yyyy-MM-dd"));
      formData.append("monthEndDate", format(monthEndDate, "yyyy-MM-dd"));
      formData.append("title", `MPR - ${formatMonthForHeader(selectedMonth)}`);

      // Add all sections data
      // Convert executiveSummary object to array format for backend
      const executiveSummariesArray = [
        {
          overallStatus: mprData.executiveSummary.overallStatus,
          keyAchievements: mprData.executiveSummary.keyAchievements,
          majorConcerns: mprData.executiveSummary.majorConcerns,
        },
      ];
      formData.append(
        "executiveSummaries",
        JSON.stringify(executiveSummariesArray),
      );
      formData.append(
        "overallProgress",
        JSON.stringify(mprData.overallProgress),
      );
      formData.append(
        "plannedVsAchieved",
        JSON.stringify(mprData.plannedVsAchieved),
      );
      formData.append(
        "manpowerSummary",
        JSON.stringify(mprData.manpowerSummary),
      );
      formData.append("plantMachinery", JSON.stringify(mprData.plantMachinery));
      formData.append(
        "materialManagement",
        JSON.stringify(mprData.materialManagement),
      );

      // Send qualityControl structure as expected by backend
      formData.append("qualityControl", JSON.stringify(mprData.qualityControl));
      formData.append("safetyHSE", JSON.stringify(mprData.safetyHSE));
      formData.append(
        "financialStatus",
        JSON.stringify(mprData.financialStatus),
      );
      formData.append(
        "drawingsApprovals",
        JSON.stringify(mprData.drawingsApprovals),
      );
      formData.append("keyIssues", JSON.stringify(mprData.keyIssues));
      formData.append(
        "decisionsRequired",
        JSON.stringify(mprData.decisionsRequired),
      );
      // Send reports metadata (titles/remarks) as JSON
      const reportsMetadata = uploadedReports.map((report) => ({
        _id: report._id,
        reportTitle: report.name || report.reportTitle,
        remarks: report.remarks || "",
      }));
      formData.append("reports", JSON.stringify(reportsMetadata));
      formData.append(
        "rfiLogs",
        JSON.stringify(
          mprData.rfiLogs.map((item) => ({
            ...item,
            issueDate: item.issueDate || null,
            expectedDate: item.expectedDate || null,
          })),
        ),
      );
      formData.append("lookAheadPlan", JSON.stringify(mprData.lookAheadPlan));

      // Add report files
      uploadedReports.forEach((file) => {
        if (file instanceof File) {
          formData.append(`reportFiles`, file);
        } else if (file && file.file) {
          formData.append(`reportFiles`, file.file);
        }
      });

      // Add photographs with shapes data
      uploadedPhotos.forEach((photo, index) => {
        if (photo.file) {
          formData.append(`photographs`, photo.file);
          formData.append(
            `photo_${index}_shapes`,
            JSON.stringify(photo.shapes || []),
          );
          formData.append(`photo_${index}_title`, photo.title || "");
        }
      });

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "multipart/form-data",
        },
      };

      // Always use add endpoint - backend handles both create and update
      const response = await axios.post(`${url}/api/mpr/add`, formData, config);

      if (response.data.message) {
        clearPersistence(); // Clear saved form data on success
        localStorage.removeItem(`mpr-photo-draft-${persistenceKey}`); // Clear photo annotation draft
        showToast(
          "success",
          "Success",
          "Monthly Progress Report saved successfully!",
        );
        setTimeout(() => navigate(-1), 1500);
      } else {
        showToast(
          "error",
          "Error",
          response.data.message || "Failed to save MPR.",
        );
      }
    } catch (err) {
      console.error("Error saving MPR:", err);
      showToast(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save report.",
      );
    } finally {
      setSaving(false);
    }
  };

  // Show toast function
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type,
      title,
      message,
    });
  };

  const closeToast = () => {
    setToast({ ...toast, open: false });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box p={2}>
        <Alert severity="warning">Project not found</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={2} sx={{ fontFamily: FONT_FAMILY }}>
        {/* ================= HEADER ================= */}
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography
              sx={{
                ...TYPOGRAPHY.H2,
                color: COLORS.PRIMARY,
                fontFamily: FONT_FAMILY,
              }}
            >
              Add Monthly Progress Report (MPR)
            </Typography>

            <IconButton size="small" onClick={() => navigate(-1)}>
              <Icon name="close3" color={COLORS.ERROR} />
            </IconButton>
          </Box>

          <Box display="flex" flexDirection="column" gap={0.4}>
            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Project Name:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {propProjectName || project.projectName}
                </span>
              </Typography>

              <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap={isMobile ? "wrap" : "nowrap"}
              >
                <Typography
                  sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
                >
                  Month:
                </Typography>

                {selectedMonth ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      size="small"
                      value={formatMonthForHeader(selectedMonth)}
                      onClick={() => setShowMonthPicker(true)}
                      sx={{
                        width: isMobile ? "100%" : "200px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "6px",
                          height: "32px",
                          pr: 0.5,
                          fontFamily: FONT_FAMILY,
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowMonthPicker(true)}
                            >
                              <Icon name="calendar" color={COLORS.SECONDARY} />
                            </IconButton>
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                    />
                    <Button
                      size="small"
                      onClick={() => setShowMonthPicker(true)}
                      sx={{ textTransform: "none", fontFamily: FONT_FAMILY }}
                    >
                      Change
                    </Button>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      borderRadius: 1,
                      px: 1.2,
                      py: 0.3,
                      gap: 0.6,
                      fontSize: TYPOGRAPHY.BODY_SMALL.fontSize,
                      color: COLORS.PRIMARY,
                      minWidth: 110,
                      justifyContent: "space-between",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: COLORS.SECONDARY,
                      },
                    }}
                    onClick={() => setShowMonthPicker(true)}
                  >
                    {selectedMonth
                      ? formatMonthForHeader(selectedMonth)
                      : "Select Month"}
                    <Icon name="calendar3" color={COLORS.SECONDARY} />
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Address:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.siteAddress || "N/A"}
                </span>
              </Typography>

              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                PMC:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {companies.find((c) => c._id === selectedCompany)?.name ||
                    "N/A"}
                </span>
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              flexWrap={isMobile ? "wrap" : "nowrap"}
              gap={1}
            >
              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Developer:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.developerName || "N/A"}
                </span>
              </Typography>

              <Typography
                sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontFamily: FONT_FAMILY }}
              >
                Engineer:{" "}
                <span style={{ color: COLORS.SECONDARY }}>
                  {project.assignedEngineer?.name ||
                    project.assignedEngineerName ||
                    "Not Assigned"}
                </span>
              </Typography>
            </Box>
          </Box>

          {/* HEADER BOTTOM LINE */}
          <Box
            mt={1.5}
            height="1px"
            sx={{ backgroundColor: COLORS.LIGHT_SECONDARY }}
          />
        </Box>

        {/* Show warning if month not selected */}
        {!selectedMonth && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Please select a month to start adding entries
          </Alert>
        )}

        {/* ================= SECTIONS ================= */}
        {selectedMonth && (
          <>
            {/* ================= 1. EXECUTIVE SUMMARY ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                1. Executive Summary
              </Typography>

              {/* Overall Project Status */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Overall Project Status
                </Typography>
                <RichTextEditor
                  value={mprData.executiveSummary.overallStatus}
                  onChange={(value) =>
                    setMprData((prev) => ({
                      ...prev,
                      executiveSummary: {
                        ...prev.executiveSummary,
                        overallStatus: value,
                      },
                    }))
                  }
                  placeholder="Describe the overall project status..."
                  height="150px"
                />
              </Box>

              {/* Key Achievements */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Key Achievements
                </Typography>
                <RichTextEditor
                  value={mprData.executiveSummary.keyAchievements}
                  onChange={(value) =>
                    setMprData((prev) => ({
                      ...prev,
                      executiveSummary: {
                        ...prev.executiveSummary,
                        keyAchievements: value,
                      },
                    }))
                  }
                  placeholder="Describe key achievements during this period..."
                  height="150px"
                />
              </Box>

              {/* Major Concerns */}
              <Box mb={2.5}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    mb: 0.8,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  Major Concerns
                </Typography>
                <RichTextEditor
                  value={mprData.executiveSummary.majorConcerns}
                  onChange={(value) =>
                    setMprData((prev) => ({
                      ...prev,
                      executiveSummary: {
                        ...prev.executiveSummary,
                        majorConcerns: value,
                      },
                    }))
                  }
                  placeholder="Describe major concerns or issues..."
                  height="150px"
                />
              </Box>
            </Box>

            {/* ================= 2. OVERALL PROGRESS SUMMARY ================= */}
            <Box mt={4}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H3,
                    color: COLORS.SECONDARY,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  2. Overall Progress Summary
                </Typography>

                {overallProgressTotal === 100 && !isOverallProgressEditMode && (
                  <Button
                    size="small"
                    startIcon={<Icon name="edit" />}
                    onClick={() => setIsOverallProgressEditMode(true)}
                    sx={{
                      color: COLORS.SECONDARY,
                      textTransform: "none",
                      fontFamily: FONT_FAMILY,
                      fontWeight: "bold",
                      "&:hover": { backgroundColor: "rgba(66, 26, 110, 0.04)" },
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Box>

              {/* View Mode: Graph only (if total is 100% and not editing) */}
              {overallProgressTotal === 100 && !isOverallProgressEditMode ? (
                <Box mb={3}>
                  <OverallProgressBarChart
                    progressItems={mprData.overallProgress}
                    label="Progress Summary (100%)"
                  />
                </Box>
              ) : (
                /* Edit Mode: Table with inputs */
                <Box mb={3}>
                  <Box
                    sx={{
                      borderBottom: `2px solid ${COLORS.SECONDARY}`,
                      pb: 1,
                      mb: 1,
                      display: "flex",
                      px: 2,
                    }}
                  >
                    <Box flex={2}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.SECONDARY,
                          fontWeight: "bold",
                        }}
                      >
                        Category
                      </Typography>
                    </Box>
                    <Box flex={1}>
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.SECONDARY,
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        Percentage (%)
                      </Typography>
                    </Box>
                    <Box width="60px">
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.SECONDARY,
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        Actions
                      </Typography>
                    </Box>
                  </Box>

                  {mprData.overallProgress.map((row, index) => (
                    <Box
                      key={index}
                      sx={{
                        px: 2,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        gap: 2,
                      }}
                    >
                      <Box flex={2}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g., Structural"
                          value={row.category}
                          onChange={(e) =>
                            handleOverallProgressChange(
                              index,
                              "category",
                              e.target.value,
                            )
                          }
                          sx={{
                            ...inputStyle,
                            "& .MuiOutlinedInput-root": {
                              height: "38px",
                              borderRadius: "4px",
                              backgroundColor: "transparent",
                              "& fieldset": { borderColor: "transparent" },
                              "&:hover fieldset": {
                                borderColor: COLORS.LIGHT_SECONDARY,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: COLORS.SECONDARY,
                              },
                            },
                            "& .MuiInputBase-input": {
                              padding: "8px 0",
                              fontFamily: FONT_FAMILY,
                              color: COLORS.SECONDARY,
                              fontWeight: 600,
                            },
                          }}
                        />
                      </Box>
                      <Box flex={1}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="0.0"
                          value={row.percentage}
                          onChange={(e) =>
                            handleOverallProgressChange(
                              index,
                              "percentage",
                              e.target.value,
                            )
                          }
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                          sx={{
                            ...inputStyle,
                            "& .MuiOutlinedInput-root": {
                              height: "38px",
                              borderRadius: "4px",
                              backgroundColor: "rgba(66, 26, 110, 0.02)",
                            },
                            "& input": {
                              textAlign: "center",
                              fontFamily: FONT_FAMILY,
                            },
                          }}
                        />
                      </Box>
                      <Box width="60px" display="flex" justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={() => removeOverallProgressRow(index)}
                          sx={{
                            color: COLORS.ERROR,
                            opacity: index < 4 ? 0.3 : 1,
                          }}
                          disabled={index < 0}
                        >
                          <Icon name="close2" size={18} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}

                  <Box
                    sx={{
                      backgroundColor: "rgba(66, 26, 110, 0.04)",
                      px: 2,
                      py: 1.5,
                      display: "flex",
                      alignItems: "center",
                      borderRadius: "4px",
                    }}
                  >
                    <Box flex={2}>
                      <Typography
                        sx={{ fontWeight: "bold", color: COLORS.SECONDARY }}
                      >
                        Total Progress
                      </Typography>
                    </Box>
                    <Box flex={1}>
                      <Typography
                        sx={{
                          textAlign: "center",
                          fontWeight: "bold",
                          color:
                            overallProgressTotal === 100
                              ? COLORS.SUCCESS
                              : COLORS.ERROR,
                          fontSize: "16px",
                        }}
                      >
                        {overallProgressTotal.toFixed(1)}%
                      </Typography>
                    </Box>
                    <Box width="60px" />
                  </Box>

                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <Box display="flex" gap={2}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Icon name="delete" />}
                        sx={clearBtnStyle}
                        onClick={() => {
                          setMprData((prev) => ({
                            ...prev,
                            overallProgress: [
                              { category: "Structural", percentage: "" },
                              { category: "MEP", percentage: "" },
                              { category: "Finishing", percentage: "" },
                              { category: "External Development", percentage: "" },
                            ],
                          }));
                          setToast({
                            open: true,
                            type: "info",
                            title: "Fields Cleared",
                            message: "Overall progress summary has been reset.",
                          });
                        }}
                      >
                        Clear Fields
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Icon name="plus2" />}
                        onClick={addOverallProgressRow}
                        sx={{
                          borderColor: COLORS.SECONDARY,
                          color: COLORS.SECONDARY,
                          textTransform: "none",
                          fontFamily: FONT_FAMILY,
                          "&:hover": {
                            borderColor: COLORS.SECONDARY,
                            backgroundColor: "rgba(66, 26, 110, 0.04)",
                          },
                        }}
                      >
                        Add New Entry
                      </Button>
                    </Box>

                    <Button
                      variant="contained"
                      size="small"
                      disabled={overallProgressTotal !== 100}
                      onClick={() => setIsOverallProgressEditMode(false)}
                      sx={{
                        backgroundColor: COLORS.SECONDARY,
                        color: "#fff",
                        textTransform: "none",
                        fontFamily: FONT_FAMILY,
                        px: 3,
                        "&:hover": { backgroundColor: COLORS.SECONDARY },
                      }}
                    >
                      View Graph
                    </Button>
                  </Box>

                  {overallProgressTotal !== 100 && (
                    <Typography
                      sx={{
                        mt: 1,
                        fontSize: "12px",
                        color: COLORS.ERROR,
                        textAlign: "right",
                        fontStyle: "italic",
                      }}
                    >
                      * Total must equal 100% to view graph
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* ================= 3. PLANNED VS ACHIEVED PROGRESS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                3. Planned vs Achieved Progress (Monthly)
              </Typography>

              {/* Table - New Format */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Activity
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Planned Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Actual Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Variance
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remark / Status
                  </Box>
                  <Box
                    width="80px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.plannedVsAchieved?.length > 0 ? (
                  mprData.plannedVsAchieved.map((row, index) => (
                    <React.Fragment key={index}>
                      <Box
                        display="flex"
                        sx={{
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          py: 1,
                          minHeight: "60px",
                        }}
                      >
                        <Box
                          flex={1.5}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.activity}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.plannedDate}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.actualDate}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.variance}
                        </Box>
                        <Box
                          flex={1.5}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.remarkStatus}
                        </Box>
                        <Box
                          width="100px"
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleEditEntry("plannedVsAchieved", index)
                            }
                            sx={{ color: "#8CD47E" }}
                          >
                            <Icon name="edit" size={16} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteEntry("plannedVsAchieved", index)
                            }
                            sx={{ color: COLORS.ERROR }}
                          >
                            <Icon name="close2" size={16} />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Inline Edit Form - Show below the clicked row */}
                      {editingEntry.section === "plannedVsAchieved" &&
                        editingEntry.index === index && (
                          <Box
                            sx={{
                              border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                              borderRadius: "8px",
                              p: 2,
                              my: 2,
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={2}
                            >
                              <Typography sx={labelStyle}>
                                Edit Planned vs Achieved Entry
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingEntry({
                                    section: null,
                                    index: null,
                                    data: null,
                                  });
                                  setInlineEditingIndex(null);
                                  // Draft will automatically load if it exists via useFormPersistence
                                }}
                                sx={{ color: COLORS.ERROR }}
                              >
                                <Icon name="close2" />
                              </IconButton>
                            </Box>

                            {/* Form Grid */}
                            <Box display="flex" gap={2} mb={2}>
                              <Box flex={1}>
                                <Typography sx={labelStyle}>
                                  Activity
                                </Typography>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={newPlannedVsAchieved.activity}
                                  onChange={(e) =>
                                    setNewPlannedVsAchieved({
                                      ...newPlannedVsAchieved,
                                      activity: e.target.value,
                                    })
                                  }
                                  sx={inputStyle}
                                />
                              </Box>
                              <Box flex={1}>
                                <Typography sx={labelStyle}>
                                  Planned Date
                                </Typography>
                                <TextField
                                  fullWidth
                                  type="date"
                                  size="small"
                                  value={newPlannedVsAchieved.plannedDate}
                                  onChange={(e) =>
                                    setNewPlannedVsAchieved({
                                      ...newPlannedVsAchieved,
                                      plannedDate: e.target.value,
                                    })
                                  }
                                  sx={inputStyle}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Box>
                            </Box>

                            <Box display="flex" gap={2} mb={2}>
                              <Box flex={1}>
                                <Typography sx={labelStyle}>
                                  Actual Date
                                </Typography>
                                <TextField
                                  fullWidth
                                  type="date"
                                  size="small"
                                  value={newPlannedVsAchieved.actualDate}
                                  onChange={(e) =>
                                    setNewPlannedVsAchieved({
                                      ...newPlannedVsAchieved,
                                      actualDate: e.target.value,
                                    })
                                  }
                                  sx={inputStyle}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Box>
                              <Box flex={1}>
                                <Typography sx={labelStyle}>
                                  Variance
                                </Typography>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={newPlannedVsAchieved.variance}
                                  onChange={(e) =>
                                    setNewPlannedVsAchieved({
                                      ...newPlannedVsAchieved,
                                      variance: e.target.value,
                                    })
                                  }
                                  sx={inputStyle}
                                />
                              </Box>
                            </Box>

                            <Box mb={2}>
                              <Typography sx={labelStyle}>
                                Remark / Status
                              </Typography>
                              <TextField
                                fullWidth
                                size="small"
                                value={newPlannedVsAchieved.remarkStatus}
                                onChange={(e) =>
                                  setNewPlannedVsAchieved({
                                    ...newPlannedVsAchieved,
                                    remarkStatus: e.target.value,
                                  })
                                }
                                sx={inputStyle}
                              />
                            </Box>

                            <Box display="flex" justifyContent="flex-end">
                              <Button
                                variant="contained"
                                startIcon={<Icon name="edit" />}
                                sx={addBtnStyle}
                                onClick={handleUpdateEntry}
                              >
                                Update Entry
                              </Button>
                            </Box>
                          </Box>
                        )}
                    </React.Fragment>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries yet. Add entries to see data.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Button Row */}
                {!showPlannedVsAchievedForm &&
                  editingEntry.section !== "plannedVsAchieved" && (
                    <Box
                      display="flex"
                      sx={{
                        py: 0.8,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Icon name="plus2" />}
                        sx={{
                          borderRadius: "7px",
                          textTransform: "none",
                          backgroundColor: COLORS.SECONDARY,
                          color: "#fff",
                          fontFamily: FONT_FAMILY,
                          px: 2.5,
                          "&:hover": {
                            backgroundColor: COLORS.PRIMARY,
                          },
                        }}
                        onClick={() => {
                          setEditingEntry({
                            section: null,
                            index: null,
                            data: null,
                          });
                          // Draft will automatically load if it exists via useFormPersistence
                          setShowPlannedVsAchievedForm(true);
                        }}
                      >
                        Add New Entry
                      </Button>
                    </Box>
                  )}

                {/* Add Form (at bottom) - ONLY for new entries */}
                {showPlannedVsAchievedForm &&
                  editingEntry.section !== "plannedVsAchieved" && (
                    <Box
                      sx={{
                        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        borderRadius: "8px",
                        p: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Typography sx={labelStyle}>
                          Add Planned vs Achieved Entry
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setShowPlannedVsAchievedForm(false)}
                          sx={{ color: COLORS.ERROR }}
                        >
                          <Icon name="close2" />
                        </IconButton>
                      </Box>

                      {/* Form Fields */}
                      <Box display="flex" gap={2} mb={2}>
                        <Box flex={1}>
                          <Typography sx={labelStyle}>Activity</Typography>
                          <TextField
                            fullWidth
                            placeholder="e.g., Foundation Work"
                            size="small"
                            value={newPlannedVsAchieved.activity}
                            onChange={(e) =>
                              setNewPlannedVsAchieved({
                                ...newPlannedVsAchieved,
                                activity: e.target.value,
                              })
                            }
                            sx={inputStyle}
                          />
                        </Box>
                        <Box flex={1}>
                          <Typography sx={labelStyle}>Planned Date</Typography>
                          <TextField
                            fullWidth
                            type="date"
                            size="small"
                            value={newPlannedVsAchieved.plannedDate}
                            onChange={(e) =>
                              setNewPlannedVsAchieved({
                                ...newPlannedVsAchieved,
                                plannedDate: e.target.value,
                              })
                            }
                            sx={inputStyle}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Box>
                      </Box>

                      <Box display="flex" gap={2} mb={2}>
                        <Box flex={1}>
                          <Typography sx={labelStyle}>Actual Date</Typography>
                          <TextField
                            fullWidth
                            type="date"
                            size="small"
                            value={newPlannedVsAchieved.actualDate}
                            onChange={(e) =>
                              setNewPlannedVsAchieved({
                                ...newPlannedVsAchieved,
                                actualDate: e.target.value,
                              })
                            }
                            sx={inputStyle}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Box>
                        <Box flex={1}>
                          <Typography sx={labelStyle}>Variance</Typography>
                          <TextField
                            fullWidth
                            placeholder="e.g., 5 days delay"
                            size="small"
                            value={newPlannedVsAchieved.variance}
                            onChange={(e) =>
                              setNewPlannedVsAchieved({
                                ...newPlannedVsAchieved,
                                variance: e.target.value,
                              })
                            }
                            sx={inputStyle}
                          />
                        </Box>
                      </Box>

                      <Box mb={2}>
                        <Typography sx={labelStyle}>Remark / Status</Typography>
                        <TextField
                          fullWidth
                          placeholder="e.g., In Progress, Completed, Pending"
                          size="small"
                          value={newPlannedVsAchieved.remarkStatus}
                          onChange={(e) =>
                            setNewPlannedVsAchieved({
                              ...newPlannedVsAchieved,
                              remarkStatus: e.target.value,
                            })
                          }
                          sx={inputStyle}
                        />
                      </Box>

                      <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                          variant="outlined"
                          startIcon={<Icon name="delete" />}
                          sx={clearBtnStyle}
                          onClick={() => {
                            if (clearNewPlannedVsAchieved) clearNewPlannedVsAchieved();
                            setNewPlannedVsAchieved({
                              activity: "",
                              plannedDate: "",
                              actualDate: "",
                              variance: "",
                              remarkStatus: "",
                            });
                          }}
                        >
                          Clear Fields
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<Icon name="plus2" />}
                          sx={addBtnStyle}
                          onClick={handleAddPlannedVsAchieved}
                        >
                          Add Entry
                        </Button>
                      </Box>
                    </Box>
                  )}
              </Box>
            </Box>

            {/* ================= 4. MANPOWER SUMMARY ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                4. Manpower Summary
              </Typography>

              {/* Table */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Category / Labour Type
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Average Manpower
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Peak Manpower
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remarks
                  </Box>
                  <Box
                    width="100px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.manpowerSummary?.length > 0 ? (
                  mprData.manpowerSummary.map((row, index) => (
                    <React.Fragment key={index}>
                      <Box
                        display="flex"
                        sx={{
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          py: 1,
                          minHeight: "60px",
                        }}
                      >
                        <Box
                          flex={1.5}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.category}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.averageManpower}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.peakManpower}
                        </Box>
                        <Box
                          flex={1.5}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.remarks}
                        </Box>
                        <Box
                          width="100px"
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleEditEntry("manpowerSummary", index);
                              setShowManpowerForm(true);
                            }}
                            sx={{ color: "#8CD47E" }}
                          >
                            <Icon name="edit" size={16} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteEntry("manpowerSummary", index)
                            }
                            sx={{ color: COLORS.ERROR }}
                          >
                            <Icon name="close2" size={16} />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Inline Edit Form - Show below the clicked row */}
                      {showManpowerForm &&
                        editingEntry.section === "manpowerSummary" &&
                        editingEntry.index === index && (
                          <Box
                            sx={{
                              border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                              borderRadius: "8px",
                              p: 2,
                              my: 2,
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={2}
                            >
                              <Typography sx={labelStyle}>
                                Edit Manpower Entry
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setShowManpowerForm(false);
                                  setEditingEntry({
                                    section: null,
                                    index: null,
                                    data: null,
                                  });
                                  setNewManpower({
                                    category: "",
                                    averageManpower: "",
                                    peakManpower: "",
                                    remarks: "",
                                  });
                                }}
                                sx={{ color: COLORS.ERROR }}
                              >
                                <Icon name="close2" />
                              </IconButton>
                            </Box>

                            <Box display="flex" gap={2} mb={2}>
                              <Box flex={1.5}>
                                <Typography sx={labelStyle}>
                                  Labour Type
                                </Typography>
                                <TextField
                                  fullWidth
                                  placeholder="e.g., Unskilled Labor"
                                  size="small"
                                  value={newManpower.category}
                                  onChange={(e) =>
                                    setNewManpower({
                                      ...newManpower,
                                      category: e.target.value,
                                    })
                                  }
                                  sx={inputStyle}
                                />
                              </Box>
                              <Box flex={1}>
                                <Typography sx={labelStyle}>
                                  Average Manpower
                                </Typography>
                                <TextField
                                  fullWidth
                                  placeholder="e.g., 5"
                                  size="small"
                                  type="number"
                                  value={newManpower.averageManpower}
                                  onChange={(e) =>
                                    setNewManpower({
                                      ...newManpower,
                                      averageManpower: e.target.value,
                                    })
                                  }
                                  sx={inputStyle}
                                />
                              </Box>
                              <Box flex={1}>
                                <Typography sx={labelStyle}>
                                  Peak Manpower
                                </Typography>
                                <TextField
                                  fullWidth
                                  placeholder="e.g., 8"
                                  size="small"
                                  type="number"
                                  value={newManpower.peakManpower}
                                  onChange={(e) =>
                                    setNewManpower({
                                      ...newManpower,
                                      peakManpower: e.target.value,
                                    })
                                  }
                                  sx={inputStyle}
                                />
                              </Box>
                            </Box>
                            <Box mb={2}>
                              <Typography sx={labelStyle}>Remarks</Typography>
                              <TextField
                                fullWidth
                                placeholder="e.g., Daily deployment"
                                size="small"
                                value={newManpower.remarks}
                                onChange={(e) =>
                                  setNewManpower({
                                    ...newManpower,
                                    remarks: e.target.value,
                                  })
                                }
                                sx={inputStyle}
                              />
                            </Box>

                            <Box display="flex" justifyContent="flex-end">
                              <Button
                                variant="contained"
                                startIcon={<Icon name="edit" />}
                                sx={addBtnStyle}
                                onClick={handleUpdateEntry}
                              >
                                Update Entry
                              </Button>
                            </Box>
                          </Box>
                        )}
                    </React.Fragment>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries yet. Add entries to see manpower details.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Row */}
                <Box
                  display="flex"
                  sx={{
                    py: 1.5,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={() => {
                      setEditingEntry({
                        section: null,
                        index: null,
                        data: null,
                      });
                      setNewManpower({
                        category: "",
                        averageManpower: "",
                        peakManpower: "",
                        remarks: "",
                      });
                      setShowManpowerForm(true);
                    }}
                  >
                    Add New Entry
                  </Button>
                </Box>
              </Box>

              {/* Add Form - Only show when NOT editing an existing entry */}
              {showManpowerForm && editingEntry.section === null && (
                <Box
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>Add Manpower Entry</Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowManpowerForm(false);
                        setEditingEntry({
                          section: null,
                          index: null,
                          data: null,
                        });
                        setNewManpower({
                          category: "",
                          averageManpower: "",
                          peakManpower: "",
                          remarks: "",
                        });
                      }}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>

                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1.5}>
                      <Typography sx={labelStyle}>Labour Type</Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g., Unskilled Labor"
                        size="small"
                        value={newManpower.category}
                        onChange={(e) =>
                          setNewManpower({
                            ...newManpower,
                            category: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Average Manpower</Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g., 5"
                        size="small"
                        type="number"
                        value={newManpower.averageManpower}
                        onChange={(e) =>
                          setNewManpower({
                            ...newManpower,
                            averageManpower: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Peak Manpower</Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g., 8"
                        size="small"
                        type="number"
                        value={newManpower.peakManpower}
                        onChange={(e) =>
                          setNewManpower({
                            ...newManpower,
                            peakManpower: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                  <Box mb={2}>
                    <Typography sx={labelStyle}>Remarks</Typography>
                    <TextField
                      fullWidth
                      placeholder="e.g., Daily deployment"
                      size="small"
                      value={newManpower.remarks}
                      onChange={(e) =>
                        setNewManpower({
                          ...newManpower,
                          remarks: e.target.value,
                        })
                      }
                      sx={inputStyle}
                    />
                  </Box>

                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={clearBtnStyle}
                      onClick={() => {
                        if (clearNewManpower) clearNewManpower();
                        setNewManpower({
                          category: "",
                          averageManpower: "",
                          peakManpower: "",
                          remarks: "",
                        });
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={handleAddManpower}
                    >
                      Add Entry
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ================= 5. MACHINERY / EQUIPMENT DEPLOYMENT ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                5. Machinery / Equipment Deployment
              </Typography>

              {/* Table - Simple like WMR */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Equipment Details
                  </Box>
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Capacity
                  </Box>
                  <Box
                    flex={0.6}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Quantity
                  </Box>
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Utilization Efficiency
                  </Box>
                  <Box
                    flex={1.2}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remarks
                  </Box>
                  <Box
                    width="120px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.plantMachinery?.length > 0 ? (
                  mprData.plantMachinery.map((row, index) => (
                    <React.Fragment key={index}>
                      {/* Display Row */}
                      <Box
                        display="flex"
                        sx={{
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          py: 1,
                          minHeight: "60px",
                        }}
                      >
                        <Box
                          flex={1.5}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.equipmentDetails}
                        </Box>
                        <Box
                          flex={0.8}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.capacity}
                        </Box>
                        <Box
                          flex={0.6}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.quantity}
                        </Box>
                        <Box
                          flex={0.8}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.utilizationEfficiency}
                        </Box>
                        <Box
                          flex={1.2}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.remarks}
                        </Box>
                        <Box
                          width="120px"
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleEditEntry("plantMachinery", index)
                            }
                            sx={{ color: "#8CD47E" }}
                          >
                            <Icon name="edit" size={16} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteEntry("plantMachinery", index)
                            }
                            sx={{ color: COLORS.ERROR }}
                          >
                            <Icon name="close2" size={16} />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Inline Edit Form - Block Style Below Row */}
                      {editingEntry.section === "plantMachinery" &&
                        editingEntry.index === index && (
                          <Box
                            sx={{
                              border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                              borderRadius: "8px",
                              p: 2,
                              my: 2,
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={2}
                            >
                              <Typography sx={labelStyle}>
                                Edit Machinery / Equipment Entry
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={handleCancelInlineEdit}
                                sx={{ color: COLORS.ERROR }}
                              >
                                <Icon name="close2" />
                              </IconButton>
                            </Box>
                            <Box display="flex" gap={2} mb={2}>
                              <Box flex={1}>
                                <Typography sx={labelStyle}>
                                  Equipment Details
                                </Typography>
                                <TextField
                                  fullWidth
                                  placeholder="Equipment details"
                                  size="small"
                                  value={newPlantMachinery.equipmentDetails}
                                  onChange={(e) =>
                                    setNewPlantMachinery({
                                      ...newPlantMachinery,
                                      equipmentDetails: e.target.value,
                                    })
                                  }
                                  sx={{
                                    ...inputStyle,
                                    "& .MuiOutlinedInput-root": {
                                      minHeight: "80px",
                                      alignItems: "flex-start",
                                    },
                                  }}
                                  multiline
                                  rows={3}
                                />
                              </Box>
                              <Box flex={0.8}>
                                <Typography sx={labelStyle}>
                                  Capacity
                                </Typography>
                                <TextField
                                  fullWidth
                                  placeholder="e.g., 10 Ton"
                                  size="small"
                                  value={newPlantMachinery.capacity}
                                  onChange={(e) =>
                                    setNewPlantMachinery({
                                      ...newPlantMachinery,
                                      capacity: e.target.value,
                                    })
                                  }
                                  sx={{
                                    ...inputStyle,
                                    "& .MuiOutlinedInput-root": {
                                      height: "48px",
                                    },
                                  }}
                                />
                              </Box>
                            </Box>
                            <Box display="flex" gap={2} mb={2}>
                              <Box flex={0.6}>
                                <Typography sx={labelStyle}>
                                  Quantity
                                </Typography>
                                <TextField
                                  fullWidth
                                  placeholder="e.g., 2"
                                  size="small"
                                  type="number"
                                  value={newPlantMachinery.quantity}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /[^0-9]/g,
                                      "",
                                    );
                                    setNewPlantMachinery({
                                      ...newPlantMachinery,
                                      quantity: value,
                                    });
                                  }}
                                  sx={{
                                    ...inputStyle,
                                    "& .MuiOutlinedInput-root": {
                                      height: "48px",
                                    },
                                  }}
                                  inputProps={{ min: 0 }}
                                />
                              </Box>
                              <Box flex={0.8}>
                                <Typography sx={labelStyle}>
                                  Utilization Efficiency (%)
                                </Typography>
                                <TextField
                                  fullWidth
                                  placeholder="e.g., 80"
                                  size="small"
                                  value={
                                    newPlantMachinery.utilizationEfficiency
                                  }
                                  onChange={(e) => {
                                    let value = e.target.value.replace(
                                      /[^0-9]/g,
                                      "",
                                    );
                                    if (value !== "") {
                                      const numValue = parseInt(value);
                                      if (numValue > 100) value = "100";
                                      if (numValue < 0) value = "0";
                                    }
                                    setNewPlantMachinery({
                                      ...newPlantMachinery,
                                      utilizationEfficiency: value,
                                    });
                                  }}
                                  sx={{
                                    ...inputStyle,
                                    "& .MuiOutlinedInput-root": {
                                      height: "48px",
                                    },
                                  }}
                                />
                              </Box>
                              <Box flex={1.2}>
                                <Typography sx={labelStyle}>Remarks</Typography>
                                <TextField
                                  fullWidth
                                  placeholder="Remarks"
                                  size="small"
                                  value={newPlantMachinery.remarks}
                                  onChange={(e) =>
                                    setNewPlantMachinery({
                                      ...newPlantMachinery,
                                      remarks: e.target.value,
                                    })
                                  }
                                  sx={{
                                    ...inputStyle,
                                    "& .MuiOutlinedInput-root": {
                                      height: "48px",
                                    },
                                  }}
                                />
                              </Box>
                            </Box>

                            <Box display="flex" justifyContent="flex-end">
                              <Button
                                variant="contained"
                                startIcon={<Icon name="edit" />}
                                sx={addBtnStyle}
                                onClick={() => handleUpdateEntry()}
                              >
                                Update Entry
                              </Button>
                            </Box>
                          </Box>
                        )}
                    </React.Fragment>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries yet.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Row */}
                {!showPlantMachineryForm && (
                  <Box
                    display="flex"
                    sx={{
                      py: 0.8,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={{
                        borderRadius: "7px",
                        textTransform: "none",
                        backgroundColor: COLORS.SECONDARY,
                        color: "#fff",
                        fontFamily: FONT_FAMILY,
                        px: 2.5,
                        "&:hover": {
                          backgroundColor: COLORS.PRIMARY,
                        },
                      }}
                      onClick={() => {
                        setEditingEntry({
                          section: null,
                          index: null,
                          data: null,
                        });
                        setNewPlantMachinery({
                          equipmentDetails: "",
                          capacity: "",
                          quantity: "",
                          utilizationEfficiency: "",
                          remarks: "",
                        });
                        setShowPlantMachineryForm(true);
                      }}
                    >
                      Add New Entry
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Add Form */}
              {showPlantMachineryForm && (
                <Box
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      Add Machinery / Equipment Entry
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowPlantMachineryForm(false)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Equipment Details</Typography>
                      <TextField
                        fullWidth
                        placeholder="Equipment details"
                        size="small"
                        value={newPlantMachinery.equipmentDetails}
                        onChange={(e) =>
                          setNewPlantMachinery({
                            ...newPlantMachinery,
                            equipmentDetails: e.target.value,
                          })
                        }
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": {
                            minHeight: "80px",
                            alignItems: "flex-start",
                          },
                        }}
                        multiline
                        rows={3}
                      />
                    </Box>
                    <Box flex={0.8}>
                      <Typography sx={labelStyle}>Capacity</Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g., 10 Ton"
                        size="small"
                        value={newPlantMachinery.capacity}
                        onChange={(e) =>
                          setNewPlantMachinery({
                            ...newPlantMachinery,
                            capacity: e.target.value,
                          })
                        }
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": {
                            height: "48px",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={0.6}>
                      <Typography sx={labelStyle}>Quantity</Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g., 2"
                        size="small"
                        type="number"
                        value={newPlantMachinery.quantity}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          setNewPlantMachinery({
                            ...newPlantMachinery,
                            quantity: value,
                          });
                        }}
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": {
                            height: "48px",
                          },
                        }}
                        inputProps={{
                          min: 0,
                        }}
                      />
                    </Box>
                    <Box flex={0.8}>
                      <Typography sx={labelStyle}>
                        Utilization Efficiency (%)
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g., 80"
                        size="small"
                        value={newPlantMachinery.utilizationEfficiency}
                        onChange={(e) => {
                          // Allow only numbers and limit to 0-100
                          let value = e.target.value.replace(/[^0-9]/g, "");
                          if (value !== "") {
                            const numValue = parseInt(value);
                            if (numValue > 100) value = "100";
                            if (numValue < 0) value = "0";
                          }
                          setNewPlantMachinery({
                            ...newPlantMachinery,
                            utilizationEfficiency: value,
                          });
                        }}
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": {
                            height: "48px",
                          },
                        }}
                        type="number"
                        inputProps={{
                          min: 0,
                          max: 100,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                      />
                    </Box>
                    <Box flex={1.2}>
                      <Typography sx={labelStyle}>Remarks</Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter remarks"
                        size="small"
                        value={newPlantMachinery.remarks}
                        onChange={(e) =>
                          setNewPlantMachinery({
                            ...newPlantMachinery,
                            remarks: e.target.value,
                          })
                        }
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": {
                            height: "48px",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={clearBtnStyle}
                      onClick={() => {
                        if (clearNewPlantMachinery) clearNewPlantMachinery();
                        setNewPlantMachinery({
                          equipmentDetails: "",
                          capacity: "",
                          quantity: "",
                          utilizationEfficiency: "",
                          remarks: "",
                        });
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={handleAddPlantMachinery}
                    >
                      Add Entry
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ================= 6. MATERIAL MANAGEMENT ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                6. Material Management
              </Typography>

              {/* Table - Simple like WMR */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Material Name
                  </Box>
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Recieve
                  </Box>
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Consumed
                  </Box>
                  <Box
                    flex={1.2}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remarks
                  </Box>
                  <Box
                    width="80px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.materialManagement?.length > 0 ? (
                  mprData.materialManagement.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.materialName}
                      </Box>
                      <Box
                        flex={0.8}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.received}
                      </Box>
                      <Box
                        flex={0.8}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.consumed}
                      </Box>
                      <Box
                        flex={1.2}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                      <Box
                        width="80px"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleEditEntry("materialManagement", index)
                          }
                          sx={{ color: "#8CD47E" }}
                        >
                          <Icon name="edit" size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDeleteEntry("materialManagement", index)
                          }
                          sx={{ color: COLORS.ERROR }}
                        >
                          <Icon name="close2" size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries yet.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Row */}
                {!showMaterialForm && (
                  <Box
                    display="flex"
                    sx={{
                      py: 0.8,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={{
                        borderRadius: "7px",
                        textTransform: "none",
                        backgroundColor: COLORS.SECONDARY,
                        color: "#fff",
                        fontFamily: FONT_FAMILY,
                        px: 2.5,
                        "&:hover": {
                          backgroundColor: COLORS.PRIMARY,
                        },
                      }}
                      onClick={() => {
                        setEditingEntry({
                          section: null,
                          index: null,
                          data: null,
                        });
                        setNewMaterial({
                          materialName: "",
                          received: "",
                          consumed: "",
                          remarks: "",
                        });
                        setShowMaterialForm(true);
                      }}
                    >
                      Add New Entry
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Add Form */}
              {showMaterialForm && (
                <Box
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      {editingEntry.section === "materialManagement"
                        ? "Edit Material Management Entry"
                        : "Add Material Management Entry"}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowMaterialForm(false)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1.5}>
                      <Typography sx={labelStyle}>Material Name</Typography>
                      <TextField
                        fullWidth
                        placeholder="Material name"
                        size="small"
                        value={newMaterial.materialName}
                        onChange={(e) =>
                          setNewMaterial({
                            ...newMaterial,
                            materialName: e.target.value,
                          })
                        }
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": {
                            height: "48px",
                          },
                        }}
                      />
                    </Box>
                    <Box flex={0.8}>
                      <Typography sx={labelStyle}>Recieve</Typography>
                      <TextField
                        fullWidth
                        placeholder="0"
                        size="small"
                        type="number"
                        value={newMaterial.received}
                        onChange={(e) =>
                          setNewMaterial({
                            ...newMaterial,
                            received: e.target.value,
                          })
                        }
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": {
                            height: "48px",
                          },
                        }}
                      />
                    </Box>
                    <Box flex={0.8}>
                      <Typography sx={labelStyle}>Consumed</Typography>
                      <TextField
                        fullWidth
                        placeholder="0"
                        size="small"
                        type="number"
                        value={newMaterial.consumed}
                        onChange={(e) =>
                          setNewMaterial({
                            ...newMaterial,
                            consumed: e.target.value,
                          })
                        }
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": {
                            height: "48px",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box mb={2}>
                    <Typography sx={labelStyle}>Remarks</Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter remarks"
                      size="small"
                      value={newMaterial.remarks}
                      onChange={(e) =>
                        setNewMaterial({
                          ...newMaterial,
                          remarks: e.target.value,
                        })
                      }
                      sx={{
                        ...inputStyle,
                        "& .MuiOutlinedInput-root": {
                          minHeight: "80px",
                          alignItems: "flex-start",
                        },
                      }}
                      multiline
                      rows={3}
                    />
                  </Box>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={clearBtnStyle}
                      onClick={() => {
                        if (clearNewMaterial) clearNewMaterial();
                        setNewMaterial({
                          materialName: "",
                          received: "",
                          consumed: "",
                          remarks: "",
                        });
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={
                        editingEntry.section === "materialManagement"
                          ? handleUpdateEntry
                          : handleAddMaterial
                      }
                    >
                      {editingEntry.section === "materialManagement"
                        ? "Update Entry"
                        : "Add Entry"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ================= 7. QUALITY ASSURANCE & QUALITY CONTROL ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                7. Quality Assurance & Quality Control
              </Typography>

              {/* 7.1 Material Testing Summary */}
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  color: COLORS.SECONDARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                  mt: 3,
                }}
              >
                7.1 Material Testing Summary
              </Typography>

              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Test Description
                  </Box>
                  <Box
                    flex={1.2}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Report ID
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Result
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Status
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remark
                  </Box>
                  <Box
                    width="80px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.qualityControl?.materialTestingSummary?.length > 0 ? (
                  mprData.qualityControl.materialTestingSummary.map(
                    (row, index) => (
                      <Box
                        key={index}
                        display="flex"
                        sx={{
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          py: 1,
                          minHeight: "60px",
                        }}
                      >
                        <Box
                          flex={1.5}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.testDescription}
                        </Box>
                        <Box
                          flex={1.2}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.testConductedDate}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.reportId}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.result}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.status}
                        </Box>
                        <Box
                          flex={1.5}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.remarks}
                        </Box>
                        <Box
                          width="80px"
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteEntry("materialTestingSummary", index)
                            }
                            sx={{ color: COLORS.ERROR }}
                          >
                            <Icon name="close2" size={16} />
                          </IconButton>
                        </Box>
                      </Box>
                    ),
                  )
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries yet.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Row */}
                <Box
                  display="flex"
                  sx={{ py: 0.8, justifyContent: "flex-end" }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={() => {
                      setEditingEntry({
                        section: null,
                        index: null,
                        data: null,
                      });
                      setNewMaterialTest({
                        testDescription: "",
                        testConductedDate: "",
                        reportId: "",
                        result: "",
                        status: "",
                        remarks: "",
                      });
                      setShowMaterialTestForm(true);
                    }}
                  >
                    Add Test Summary
                  </Button>
                </Box>
              </Box>

              {/* Material Test Form */}
              {showMaterialTestForm && (
                <Box
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      Add Material Test Entry
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowMaterialTestForm(false)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={2}>
                      <Typography sx={labelStyle}>Test Description</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={newMaterialTest.testDescription}
                        onChange={(e) =>
                          setNewMaterialTest({
                            ...newMaterialTest,
                            testDescription: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Date</Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        value={newMaterialTest.testConductedDate}
                        onChange={(e) =>
                          setNewMaterialTest({
                            ...newMaterialTest,
                            testConductedDate: e.target.value,
                          })
                        }
                        sx={inputStyle}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Report ID</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={newMaterialTest.reportId}
                        onChange={(e) =>
                          setNewMaterialTest({
                            ...newMaterialTest,
                            reportId: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Result</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={newMaterialTest.result}
                        onChange={(e) =>
                          setNewMaterialTest({
                            ...newMaterialTest,
                            result: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Status</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={newMaterialTest.status}
                        onChange={(e) =>
                          setNewMaterialTest({
                            ...newMaterialTest,
                            status: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                  <Box mb={2}>
                    <Typography sx={labelStyle}>Remark</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      value={newMaterialTest.remarks}
                      onChange={(e) =>
                        setNewMaterialTest({
                          ...newMaterialTest,
                          remarks: e.target.value,
                        })
                      }
                      sx={inputStyle}
                    />
                  </Box>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={clearBtnStyle}
                      onClick={() => {
                        if (clearNewMaterialTest) clearNewMaterialTest();
                        setNewMaterialTest({
                          testDescription: "",
                          testConductedDate: "",
                          reportId: "",
                          result: "",
                          status: "",
                          remarks: "",
                        });
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={handleAddMaterialTest}
                    >
                      Add Entry
                    </Button>
                  </Box>
                </Box>
              )}

              {/* 7.2 Inspection & NCR Summary */}
              <Typography
                sx={{
                  ...TYPOGRAPHY.H4,
                  color: COLORS.SECONDARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                  mt: 4,
                }}
              >
                7.2 Inspection & NCR Summary
              </Typography>

              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    NCR No.
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Category
                  </Box>
                  <Box
                    flex={2}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Description
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Status
                  </Box>
                  <Box
                    width="80px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.qualityControl?.inspectionAndNCRSummary?.length > 0 ? (
                  mprData.qualityControl.inspectionAndNCRSummary.map(
                    (row, index) => (
                      <Box
                        key={index}
                        display="flex"
                        sx={{
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          py: 1,
                          minHeight: "60px",
                        }}
                      >
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.ncrNo}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.date}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.category}
                        </Box>
                        <Box
                          flex={2}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.description}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.status}
                        </Box>
                        <Box
                          width="80px"
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteEntry(
                                "inspectionAndNCRSummary",
                                index,
                              )
                            }
                            sx={{ color: COLORS.ERROR }}
                          >
                            <Icon name="close2" size={16} />
                          </IconButton>
                        </Box>
                      </Box>
                    ),
                  )
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries yet.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Row */}
                <Box
                  display="flex"
                  sx={{ py: 0.8, justifyContent: "flex-end" }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={() => {
                      setEditingEntry({
                        section: null,
                        index: null,
                        data: null,
                      });
                      setNewInspectionNCR({
                        ncrNo: "",
                        date: "",
                        category: "",
                        description: "",
                        status: "",
                      });
                      setShowInspectionNCRForm(true);
                    }}
                  >
                    Add Inspection/NCR
                  </Button>
                </Box>
              </Box>

              {/* Inspection/NCR Form */}
              {showInspectionNCRForm && (
                <Box
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      Add Inspection & NCR Entry
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowInspectionNCRForm(false)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>NCR No.</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={newInspectionNCR.ncrNo}
                        onChange={(e) =>
                          setNewInspectionNCR({
                            ...newInspectionNCR,
                            ncrNo: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Date</Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        value={newInspectionNCR.date}
                        onChange={(e) =>
                          setNewInspectionNCR({
                            ...newInspectionNCR,
                            date: e.target.value,
                          })
                        }
                        sx={inputStyle}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Category</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={newInspectionNCR.category}
                        onChange={(e) =>
                          setNewInspectionNCR({
                            ...newInspectionNCR,
                            category: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                  <Box mb={2}>
                    <Typography sx={labelStyle}>Description</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      value={newInspectionNCR.description}
                      onChange={(e) =>
                        setNewInspectionNCR({
                          ...newInspectionNCR,
                          description: e.target.value,
                        })
                      }
                      sx={inputStyle}
                    />
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Status</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={newInspectionNCR.status}
                        onChange={(e) =>
                          setNewInspectionNCR({
                            ...newInspectionNCR,
                            status: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={clearBtnStyle}
                      onClick={() => {
                        if (clearNewInspectionNCR) clearNewInspectionNCR();
                        setNewInspectionNCR({
                          ncrNo: "",
                          date: "",
                          category: "",
                          description: "",
                          status: "",
                        });
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={
                        editingEntry.section === "inspectionAndNCRSummary"
                          ? handleUpdateEntry
                          : handleAddInspectionNCR
                      }
                    >
                      {editingEntry.section === "inspectionAndNCRSummary"
                        ? "Update Entry"
                        : "Add Entry"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ================= 8. SAFETY, HEALTH & ENVIRONMENT (SHE) ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                8. Safety, Health & Environment (SHE)
              </Typography>

              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Date
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Parameter
                  </Box>
                  <Box
                    flex={2}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Description
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Status
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remark
                  </Box>
                  <Box
                    width="80px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.safetyHSE?.length > 0 ? (
                  mprData.safetyHSE.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.date}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.parameter}
                      </Box>
                      <Box
                        flex={2}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.description}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color:
                            row.status === "Closed"
                              ? COLORS.SUCCESS
                              : COLORS.ERROR,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.status}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                      <Box
                        width="80px"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleEditEntry("safetyHSE", index)}
                          sx={{ color: "#8CD47E" }}
                        >
                          <Icon name="edit" size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEntry("safetyHSE", index)}
                          sx={{ color: COLORS.ERROR }}
                        >
                          <Icon name="close2" size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries yet.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Button Row */}
                {!showSafetyForm && (
                  <Box
                    display="flex"
                    sx={{ py: 0.8, justifyContent: "flex-end" }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={() => {
                        setEditingEntry({
                          section: null,
                          index: null,
                          data: null,
                        });
                        setNewSafety({
                          date: "",
                          parameter: "",
                          description: "",
                          status: "Open",
                          remarks: "",
                        });
                        setShowSafetyForm(true);
                      }}
                    >
                      Add New Entry
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Add/Edit Form */}
              {showSafetyForm && (
                <Box
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      {editingEntry.section === "safetyHSE"
                        ? "Edit Safety Entry"
                        : "Add Safety Entry"}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowSafetyForm(false)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Date</Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        value={newSafety.date}
                        onChange={(e) =>
                          setNewSafety({ ...newSafety, date: e.target.value })
                        }
                        sx={inputStyle}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                    <Box flex={1.5}>
                      <Typography sx={labelStyle}>Parameter</Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g. Safety Audit"
                        size="small"
                        value={newSafety.parameter}
                        onChange={(e) =>
                          setNewSafety({
                            ...newSafety,
                            parameter: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Status</Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={newSafety.status}
                        onChange={(e) =>
                          setNewSafety({ ...newSafety, status: e.target.value })
                        }
                        sx={inputStyle}
                      >
                        <MenuItem value="Open">Open</MenuItem>
                        <MenuItem value="Closed">Closed</MenuItem>
                      </TextField>
                    </Box>
                  </Box>
                  <Box mb={2}>
                    <Typography sx={labelStyle}>Description</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      value={newSafety.description}
                      onChange={(e) =>
                        setNewSafety({
                          ...newSafety,
                          description: e.target.value,
                        })
                      }
                      sx={inputStyle}
                    />
                  </Box>
                  <Box mb={2}>
                    <Typography sx={labelStyle}>Remark</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      value={newSafety.remarks}
                      onChange={(e) =>
                        setNewSafety({ ...newSafety, remarks: e.target.value })
                      }
                      sx={inputStyle}
                    />
                  </Box>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={clearBtnStyle}
                      onClick={() => {
                        if (clearNewSafety) clearNewSafety();
                        setNewSafety({
                          date: "",
                          parameter: "",
                          description: "",
                          status: "Open",
                          remarks: "",
                        });
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={
                        editingEntry.section === "safetyHSE"
                          ? handleUpdateEntry
                          : handleAddSafety
                      }
                    >
                      {editingEntry.section === "safetyHSE"
                        ? "Update Entry"
                        : "Add Entry"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ================= 9. FINANCIAL STATUS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                9. Financial Status
              </Typography>

              {/* Table - Simple like WMR */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Description
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Amount Submitted
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Amount Certified
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      mr: 2,
                    }}
                  >
                    Payment Received
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                    }}
                  >
                    Remarks
                  </Box>
                  <Box
                    width="80px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.financialStatus?.length > 0 ? (
                  mprData.financialStatus.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.description}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.amountSubmitted}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.amountCertified}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                          mr: 2,
                        }}
                      >
                        {row.paymentReceived}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                      <Box
                        width="80px"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleEditEntry("financialStatus", index)
                          }
                          sx={{ color: "#8CD47E" }}
                        >
                          <Icon name="edit" size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDeleteEntry("financialStatus", index)
                          }
                          sx={{ color: COLORS.ERROR }}
                        >
                          <Icon name="close2" size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries yet.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Row */}
                {!showFinancialForm && (
                  <Box
                    display="flex"
                    sx={{
                      py: 0.8,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={{
                        borderRadius: "7px",
                        textTransform: "none",
                        backgroundColor: COLORS.SECONDARY,
                        color: "#fff",
                        fontFamily: FONT_FAMILY,
                        px: 2.5,
                        "&:hover": {
                          backgroundColor: COLORS.PRIMARY,
                        },
                      }}
                      onClick={() => {
                        setEditingEntry({
                          section: null,
                          index: null,
                          data: null,
                        });
                        setNewFinancial({
                          description: "",
                          amountSubmitted: "",
                          amountCertified: "",
                          paymentReceived: "",
                          remarks: "",
                        });
                        setShowFinancialForm(true);
                      }}
                    >
                      Add New Entry
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Add Form */}
              {showFinancialForm && (
                <Box
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      Add Financial Status Entry
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowFinancialForm(false)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1.5}>
                      <Typography sx={labelStyle}>Description</Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g. RA Bill - 04"
                        size="small"
                        value={newFinancial.description}
                        onChange={(e) =>
                          setNewFinancial({
                            ...newFinancial,
                            description: e.target.value,
                          })
                        }
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": { minHeight: "40px" },
                        }}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>
                        Submitted (Amount)
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="0"
                        size="small"
                        type="number"
                        value={newFinancial.amountSubmitted}
                        onChange={(e) =>
                          setNewFinancial({
                            ...newFinancial,
                            amountSubmitted: e.target.value,
                          })
                        }
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": { minHeight: "40px" },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>
                        Certified (Amount)
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="0"
                        size="small"
                        type="number"
                        value={newFinancial.amountCertified}
                        onChange={(e) =>
                          setNewFinancial({
                            ...newFinancial,
                            amountCertified: e.target.value,
                          })
                        }
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": { minHeight: "40px" },
                        }}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Payment Received</Typography>
                      <TextField
                        fullWidth
                        placeholder="0"
                        size="small"
                        type="number"
                        value={newFinancial.paymentReceived}
                        onChange={(e) =>
                          setNewFinancial({
                            ...newFinancial,
                            paymentReceived: e.target.value,
                          })
                        }
                        sx={{
                          ...inputStyle,
                          "& .MuiOutlinedInput-root": { minHeight: "40px" },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box mb={2}>
                    <Typography sx={labelStyle}>Remarks</Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter remarks"
                      size="small"
                      value={newFinancial.remarks}
                      onChange={(e) =>
                        setNewFinancial({
                          ...newFinancial,
                          remarks: e.target.value,
                        })
                      }
                      sx={{
                        ...inputStyle,
                        "& .MuiOutlinedInput-root": {
                          minHeight: "60px",
                          alignItems: "flex-start",
                        },
                      }}
                      multiline
                      rows={2}
                    />
                  </Box>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={clearBtnStyle}
                      onClick={() => {
                        if (clearNewFinancial) clearNewFinancial();
                        setNewFinancial({
                          description: "",
                          amountSubmitted: "",
                          amountCertified: "",
                          paymentReceived: "",
                          remarks: "",
                        });
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={
                        editingEntry.section === "financialStatus"
                          ? handleUpdateEntry
                          : handleAddFinancial
                      }
                    >
                      {editingEntry.section === "financialStatus"
                        ? "Update Entry"
                        : "Add Entry"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ================= 10. DRAWINGS & APPROVALS STATUS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                10. Drawings & Approvals Status
              </Typography>

              {/* 10.1 GFC Drawing Received */}
              <Box mb={3}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    color: COLORS.PRIMARY,
                    fontFamily: FONT_FAMILY,
                    mb: 1.5,
                  }}
                >
                  10.1 GFC Drawing Received
                </Typography>

                {/* GFC Table Header */}
                <Box mb={1}>
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 0.8,
                    }}
                  >
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Category
                    </Box>
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Drawing Type
                    </Box>
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Drawing No.
                    </Box>
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Revision
                    </Box>
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Received Date
                    </Box>
                    <Box
                      flex={1.5}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Remark / Status
                    </Box>
                    <Box
                      width="80px"
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        textAlign: "center",
                        px: 1,
                      }}
                    >
                      Actions
                    </Box>
                  </Box>
                </Box>

                {/* GFC Table Body */}
                {mprData.drawingsApprovals?.gfcDrawingReceived?.length > 0 ? (
                  mprData.drawingsApprovals.gfcDrawingReceived.map(
                    (row, index) => (
                      <Box
                        key={index}
                        display="flex"
                        sx={{
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          py: 1,
                          minHeight: "60px",
                        }}
                      >
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.category}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.drawingType}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.drawingNo}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.revision}
                        </Box>
                        <Box
                          flex={1}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.receivedDate
                            ? new Date(row.receivedDate).toLocaleDateString()
                            : ""}
                        </Box>
                        <Box
                          flex={1.5}
                          sx={{
                            ...TYPOGRAPHY.BODY_LARGE,
                            fontFamily: FONT_FAMILY,
                            color: COLORS.SECONDARY,
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                            minHeight: "40px",
                          }}
                        >
                          {row.remark || row.status}
                        </Box>
                        <Box
                          width="80px"
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleEditEntry("gfcDrawingReceived", index)
                            }
                            sx={{ color: "#8CD47E" }}
                          >
                            <Icon name="edit" size={16} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteEntry("gfcDrawingReceived", index)
                            }
                            sx={{ color: COLORS.ERROR }}
                          >
                            <Icon name="close2" size={16} />
                          </IconButton>
                        </Box>
                      </Box>
                    ),
                  )
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No GFC drawings received yet.
                    </Typography>
                  </Box>
                )}

                {/* GFC Add New Entry Button */}
                <Box
                  display="flex"
                  sx={{ py: 0.8, justifyContent: "flex-end" }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={() => {
                      setEditingEntry({
                        section: "gfcDrawingReceived",
                        index: null,
                        data: null,
                      });
                      setNewDrawings({
                        gfcCategory: "",
                        gfcDrawingType: "",
                        gfcDrawingNo: "",
                        gfcRevision: "",
                        gfcReceivedDate: "",
                        gfcRemarkStatus: "",
                        pendingCategory: "",
                        pendingDrawingType: "",
                        pendingExpectedDate: "",
                        pendingCurrentStatus: "",
                        pendingRemark: "",
                      });
                      setShowDrawingsForm(true);
                    }}
                  >
                    Add New Entry
                  </Button>
                </Box>

                {/* GFC Drawing Form */}
                {showDrawingsForm &&
                  editingEntry.section === "gfcDrawingReceived" && (
                    <Box
                      sx={{
                        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        borderRadius: "8px",
                        p: 2,
                        mb: 2,
                        mt: 2,
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Typography sx={labelStyle}>
                          {editingEntry.index !== null
                            ? "Edit GFC Drawing Entry"
                            : "Add GFC Drawing Entry"}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowDrawingsForm(false);
                            setEditingEntry({
                              section: null,
                              index: null,
                              data: null,
                            });
                          }}
                          sx={{ color: COLORS.ERROR }}
                        >
                          <Icon name="close2" />
                        </IconButton>
                      </Box>
                      <Box mb={3}>
                        <Typography
                          sx={{
                            ...TYPOGRAPHY.H4,
                            color: COLORS.PRIMARY,
                            fontFamily: FONT_FAMILY,
                            mb: 1.5,
                          }}
                        >
                          GFC Drawing Received
                        </Typography>
                        <Box display="flex" gap={2} mb={2}>
                          <Box flex={1}>
                            <Typography sx={labelStyle}>Category</Typography>
                            <TextField
                              fullWidth
                              select
                              size="small"
                              value={newDrawings.gfcCategory}
                              onChange={(e) =>
                                setNewDrawings({
                                  ...newDrawings,
                                  gfcCategory: e.target.value,
                                })
                              }
                              sx={inputStyle}
                            >
                              {drawingCategoryOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </TextField>
                            {newDrawings.gfcCategory === "Other" && (
                              <TextField
                                fullWidth
                                placeholder="Specify designation"
                                size="small"
                                value={newDrawings.gfcOtherCategory || ""}
                                onChange={(e) =>
                                  setNewDrawings({
                                    ...newDrawings,
                                    gfcOtherCategory: e.target.value,
                                  })
                                }
                                sx={{ ...inputStyle, mt: 1 }}
                              />
                            )}
                          </Box>
                          <Box flex={1}>
                            <Typography sx={labelStyle}>
                              Drawing Type
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder="e.g. Architectural, Structural"
                              size="small"
                              value={newDrawings.gfcDrawingType}
                              onChange={(e) =>
                                setNewDrawings({
                                  ...newDrawings,
                                  gfcDrawingType: e.target.value,
                                })
                              }
                              sx={inputStyle}
                            />
                          </Box>
                          <Box flex={1}>
                            <Typography sx={labelStyle}>Drawing No.</Typography>
                            <TextField
                              fullWidth
                              placeholder="Drawing number"
                              size="small"
                              value={newDrawings.gfcDrawingNo}
                              onChange={(e) =>
                                setNewDrawings({
                                  ...newDrawings,
                                  gfcDrawingNo: e.target.value,
                                })
                              }
                              sx={inputStyle}
                            />
                          </Box>
                        </Box>
                        <Box display="flex" gap={2} mb={2}>
                          <Box flex={1}>
                            <Typography sx={labelStyle}>Revision</Typography>
                            <TextField
                              fullWidth
                              placeholder="Revision number"
                              size="small"
                              value={newDrawings.gfcRevision}
                              onChange={(e) =>
                                setNewDrawings({
                                  ...newDrawings,
                                  gfcRevision: e.target.value,
                                })
                              }
                              sx={inputStyle}
                            />
                          </Box>
                          <Box flex={1}>
                            <Typography sx={labelStyle}>
                              Received Date
                            </Typography>
                            <TextField
                              fullWidth
                              type="date"
                              size="small"
                              value={newDrawings.gfcReceivedDate}
                              onChange={(e) =>
                                setNewDrawings({
                                  ...newDrawings,
                                  gfcReceivedDate: e.target.value,
                                })
                              }
                              sx={inputStyle}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Box>
                          <Box flex={1.5}>
                            <Typography sx={labelStyle}>
                              Remark / Status
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder="Remarks or status"
                              size="small"
                              value={newDrawings.gfcRemarkStatus}
                              onChange={(e) =>
                                setNewDrawings({
                                  ...newDrawings,
                                  gfcRemarkStatus: e.target.value,
                                })
                              }
                              sx={inputStyle}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                          variant="outlined"
                          startIcon={<Icon name="delete" />}
                          sx={clearBtnStyle}
                          onClick={() => {
                            if (clearNewDrawings) clearNewDrawings();
                            setNewDrawings({
                              gfcCategory: "",
                              gfcDrawingType: "",
                              gfcDrawingNo: "",
                              gfcRevision: "",
                              gfcReceivedDate: "",
                              gfcRemarkStatus: "",
                              pendingCategory: "",
                              pendingDrawingType: "",
                              pendingExpectedDate: "",
                              pendingCurrentStatus: "",
                              pendingRemark: "",
                            });
                          }}
                        >
                          Clear Fields
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={
                            editingEntry.index !== null ? (
                              <Icon name="edit" />
                            ) : (
                              <Icon name="plus2" />
                            )
                          }
                          sx={addBtnStyle}
                          onClick={
                            editingEntry.index !== null
                              ? handleUpdateEntry
                              : handleAddDrawings
                          }
                        >
                          {editingEntry.index !== null
                            ? "Update Entry"
                            : "Add Entry"}
                        </Button>
                      </Box>
                    </Box>
                  )}
              </Box>

              {/* 10.2 Pending Drawings */}
              <Box mb={3}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H4,
                    color: COLORS.PRIMARY,
                    fontFamily: FONT_FAMILY,
                    mb: 1.5,
                  }}
                >
                  10.2 Pending Drawings
                </Typography>

                {/* Pending Table Header */}
                <Box mb={1}>
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 0.8,
                    }}
                  >
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Category
                    </Box>
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Drawing Type
                    </Box>
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Expected Date
                    </Box>
                    <Box
                      flex={1}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Current Status
                    </Box>
                    <Box
                      flex={1.5}
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        px: 1,
                      }}
                    >
                      Remark
                    </Box>
                    <Box
                      width="80px"
                      sx={{
                        ...TYPOGRAPHY.BODY_LARGE,
                        fontFamily: FONT_FAMILY,
                        color: COLORS.TEXT,
                        fontWeight: "bold",
                        textAlign: "center",
                        px: 1,
                      }}
                    >
                      Actions
                    </Box>
                  </Box>
                </Box>

                {/* Pending Table Body */}
                {mprData.drawingsApprovals?.pendingDrawing?.length > 0 ? (
                  mprData.drawingsApprovals.pendingDrawing.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.category}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.drawingType}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.expectedDate
                          ? new Date(row.expectedDate).toLocaleDateString()
                          : ""}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.currentStatus}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remark}
                      </Box>
                      <Box
                        width="80px"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleEditEntry("pendingDrawing", index)
                          }
                          sx={{ color: "#8CD47E" }}
                        >
                          <Icon name="edit" size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDeleteEntry("pendingDrawing", index)
                          }
                          sx={{ color: COLORS.ERROR }}
                        >
                          <Icon name="close2" size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No pending drawings.
                    </Typography>
                  </Box>
                )}

                {/* Pending Add New Entry Button */}
                <Box
                  display="flex"
                  sx={{ py: 0.8, justifyContent: "flex-end" }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Icon name="plus2" />}
                    sx={addBtnStyle}
                    onClick={() => {
                      setEditingEntry({
                        section: "pendingDrawing",
                        index: null,
                        data: null,
                      });
                      setNewDrawings({
                        gfcCategory: "",
                        gfcDrawingType: "",
                        gfcDrawingNo: "",
                        gfcRevision: "",
                        gfcReceivedDate: "",
                        gfcRemarkStatus: "",
                        pendingCategory: "",
                        pendingDrawingType: "",
                        pendingExpectedDate: "",
                        pendingCurrentStatus: "",
                        pendingRemark: "",
                      });
                      setShowDrawingsForm(true);
                    }}
                  >
                    Add New Entry
                  </Button>
                </Box>

                {/* Pending Drawing Form */}
                {showDrawingsForm &&
                  editingEntry.section === "pendingDrawing" && (
                    <Box
                      sx={{
                        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        borderRadius: "8px",
                        p: 2,
                        mb: 2,
                        mt: 2,
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Typography sx={labelStyle}>
                          {editingEntry.index !== null
                            ? "Edit Pending Drawing Entry"
                            : "Add Pending Drawing Entry"}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowDrawingsForm(false);
                            setEditingEntry({
                              section: null,
                              index: null,
                              data: null,
                            });
                          }}
                          sx={{ color: COLORS.ERROR }}
                        >
                          <Icon name="close2" />
                        </IconButton>
                      </Box>
                      <Box mb={2}>
                        <Typography
                          sx={{
                            ...TYPOGRAPHY.H4,
                            color: COLORS.PRIMARY,
                            fontFamily: FONT_FAMILY,
                            mb: 1.5,
                          }}
                        >
                          Pending Drawings
                        </Typography>
                        <Box display="flex" gap={2} mb={2}>
                          <Box flex={1}>
                            <Typography sx={labelStyle}>Category</Typography>
                            <TextField
                              fullWidth
                              select
                              size="small"
                              value={newDrawings.pendingCategory}
                              onChange={(e) =>
                                setNewDrawings({
                                  ...newDrawings,
                                  pendingCategory: e.target.value,
                                })
                              }
                              sx={inputStyle}
                            >
                              {drawingCategoryOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </TextField>
                            {newDrawings.pendingCategory === "Other" && (
                              <TextField
                                fullWidth
                                placeholder="Specify designation"
                                size="small"
                                value={newDrawings.pendingOtherCategory || ""}
                                onChange={(e) =>
                                  setNewDrawings({
                                    ...newDrawings,
                                    pendingOtherCategory: e.target.value,
                                  })
                                }
                                sx={{ ...inputStyle, mt: 1 }}
                              />
                            )}
                          </Box>
                          <Box flex={1}>
                            <Typography sx={labelStyle}>
                              Drawing Type
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder="e.g. Architectural, Structural"
                              size="small"
                              value={newDrawings.pendingDrawingType}
                              onChange={(e) =>
                                setNewDrawings({
                                  ...newDrawings,
                                  pendingDrawingType: e.target.value,
                                })
                              }
                              sx={inputStyle}
                            />
                          </Box>
                          <Box flex={1}>
                            <Typography sx={labelStyle}>
                              Expected Date
                            </Typography>
                            <TextField
                              fullWidth
                              type="date"
                              size="small"
                              value={newDrawings.pendingExpectedDate}
                              onChange={(e) =>
                                setNewDrawings({
                                  ...newDrawings,
                                  pendingExpectedDate: e.target.value,
                                })
                              }
                              sx={inputStyle}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Box>
                        </Box>
                        <Box display="flex" gap={2} mb={2}>
                          <Box flex={1}>
                            <Typography sx={labelStyle}>
                              Current Status
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder="Current status"
                              size="small"
                              value={newDrawings.pendingCurrentStatus}
                              onChange={(e) =>
                                setNewDrawings({
                                  ...newDrawings,
                                  pendingCurrentStatus: e.target.value,
                                })
                              }
                              sx={inputStyle}
                            />
                          </Box>
                          <Box flex={1.5}>
                            <Typography sx={labelStyle}>Remark</Typography>
                            <TextField
                              fullWidth
                              placeholder="Remarks"
                              size="small"
                              value={newDrawings.pendingRemark}
                              onChange={(e) =>
                                setNewDrawings({
                                  ...newDrawings,
                                  pendingRemark: e.target.value,
                                })
                              }
                              sx={inputStyle}
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                          variant="outlined"
                          startIcon={<Icon name="delete" />}
                          sx={clearBtnStyle}
                          onClick={() => {
                            if (clearNewDrawings) clearNewDrawings();
                            setNewDrawings({
                              gfcCategory: "",
                              gfcDrawingType: "",
                              gfcDrawingNo: "",
                              gfcRevision: "",
                              gfcReceivedDate: "",
                              gfcRemarkStatus: "",
                              pendingCategory: "",
                              pendingDrawingType: "",
                              pendingExpectedDate: "",
                              pendingCurrentStatus: "",
                              pendingRemark: "",
                            });
                          }}
                        >
                          Clear Fields
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={
                            editingEntry.index !== null ? (
                              <Icon name="edit" />
                            ) : (
                              <Icon name="plus2" />
                            )
                          }
                          sx={addBtnStyle}
                          onClick={
                            editingEntry.index !== null
                              ? handleUpdateEntry
                              : handleAddDrawings
                          }
                        >
                          {editingEntry.index !== null
                            ? "Update Entry"
                            : "Add Entry"}
                        </Button>
                      </Box>
                    </Box>
                  )}
              </Box>
            </Box>

            {/* ================= 11. KEY ISSUES & RISK AREAS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                11. Key Issues & Risk Areas
              </Typography>

              {/* Table - Simple like WMR */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Issues Description
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Reasons for Delay
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Mitigation measures
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Impact level
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Remarks
                  </Box>
                  <Box
                    width="80px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.keyIssues?.length > 0 ? (
                  mprData.keyIssues.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.issueDescription}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.delayReasons}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.mitigationMeasures}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.impact}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                      <Box
                        width="80px"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleEditEntry("keyIssues", index)}
                          sx={{ color: "#8CD47E" }}
                        >
                          <Icon name="edit" size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEntry("keyIssues", index)}
                          sx={{ color: COLORS.ERROR }}
                        >
                          <Icon name="close2" size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries yet.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Row */}
                {!showIssuesForm && (
                  <Box
                    display="flex"
                    sx={{
                      py: 0.8,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={{
                        borderRadius: "7px",
                        textTransform: "none",
                        backgroundColor: COLORS.SECONDARY,
                        color: "#fff",
                        fontFamily: FONT_FAMILY,
                        px: 2.5,
                        "&:hover": {
                          backgroundColor: COLORS.PRIMARY,
                        },
                      }}
                      onClick={() => {
                        setEditingEntry({
                          section: null,
                          index: null,
                          data: null,
                        });
                        setNewIssues({
                          issueDescription: "",
                          delayReasons: "",
                          mitigationMeasures: "",
                          impact: "",
                          remarks: "",
                        });
                        setShowIssuesForm(true);
                      }}
                    >
                      Add New Entry
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Add Form */}
              {showIssuesForm && (
                <Box
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      Add Key Issues Entry
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowIssuesForm(false)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>
                        Issues Description
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Describe the issue"
                        size="small"
                        value={newIssues.issueDescription}
                        onChange={(e) =>
                          setNewIssues({
                            ...newIssues,
                            issueDescription: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Reasons for Delay</Typography>
                      <TextField
                        fullWidth
                        placeholder="Reasons for delay"
                        size="small"
                        value={newIssues.delayReasons}
                        onChange={(e) =>
                          setNewIssues({
                            ...newIssues,
                            delayReasons: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>
                        Mitigation Measures
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Mitigation measures"
                        size="small"
                        value={newIssues.mitigationMeasures}
                        onChange={(e) =>
                          setNewIssues({
                            ...newIssues,
                            mitigationMeasures: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Impact Level</Typography>
                      <TextField
                        fullWidth
                        placeholder="Impact level (e.g., High, Medium, Low)"
                        size="small"
                        value={newIssues.impact}
                        onChange={(e) =>
                          setNewIssues({ ...newIssues, impact: e.target.value })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Remarks</Typography>
                      <TextField
                        fullWidth
                        placeholder="Additional remarks"
                        size="small"
                        value={newIssues.remarks}
                        onChange={(e) =>
                          setNewIssues({
                            ...newIssues,
                            remarks: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={clearBtnStyle}
                      onClick={() => {
                        if (clearNewIssues) clearNewIssues();
                        setNewIssues({
                          issueDescription: "",
                          delayReasons: "",
                          mitigationMeasures: "",
                          impact: "",
                          remarks: "",
                        });
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={
                        editingEntry.section === "keyIssues"
                          ? handleUpdateEntry
                          : handleAddIssues
                      }
                    >
                      {editingEntry.section === "keyIssues"
                        ? "Update Entry"
                        : "Add Entry"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ================= 12. REPORTS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  fontFamily: FONT_FAMILY,
                  color: COLORS.PRIMARY,
                  mb: 2,
                }}
              >
                12. Reports
              </Typography>

              {uploadedReports.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {uploadedReports.map((file, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        borderRadius: "8px",
                        px: 2,
                        py: 1,
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{ fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}
                      >
                        {file.name}
                      </Typography>
                      <Box display="flex" gap={1}>
                        {/* FIXED: Added proper URL construction with forward slash */}
                        <IconButton
                          onClick={() => {
                            if (file.filePath) {
                              const fileUrl = `${url}/${file.filePath.replace(/\\/g, "/").replace(/^\//, "")}`;
                              window.open(fileUrl, "_blank");
                            } else if (file instanceof File) {
                              const fileUrl = URL.createObjectURL(file);
                              window.open(fileUrl, "_blank");
                            }
                          }}
                        >
                          <Icon name="Action1" />
                        </IconButton>
                        <IconButton onClick={() => handleRemoveReport(index)}>
                          <Icon name="close2" color={COLORS.ERROR} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              <Box mb={2}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<Icon name="plus2" />}
                  sx={{
                    borderRadius: "7px",
                    textTransform: "none",
                    backgroundColor: COLORS.SECONDARY,
                    color: "#fff",
                    fontFamily: FONT_FAMILY,
                    px: 2.5,
                    "&:hover": {
                      backgroundColor: COLORS.PRIMARY,
                    },
                  }}
                >
                  Upload Reports
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={handleReportUpload}
                  />
                </Button>
              </Box>
            </Box>

            {/* ================= 13. DECISIONS REQUIRED ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                13. Decisions Required
              </Typography>

              {/* Table */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Decision Topic
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Required By Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Responsibility
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Remark
                  </Box>
                  <Box
                    width="80px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.decisionsRequired?.length > 0 ? (
                  mprData.decisionsRequired.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.decisionTopic}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.requiredByDate
                          ? format(new Date(row.requiredByDate), "dd/MM/yyyy")
                          : ""}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.responsibility}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                      <Box
                        width="80px"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleEditEntry("decisionsRequired", index)
                          }
                          sx={{ color: "#8CD47E" }}
                        >
                          <Icon name="edit" size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDeleteEntry("decisionsRequired", index)
                          }
                          sx={{ color: COLORS.ERROR }}
                        >
                          <Icon name="close2" size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries yet.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Button Row */}
                {!showDecisionsForm && (
                  <Box
                    display="flex"
                    sx={{ py: 0.8, justifyContent: "flex-end" }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={() => {
                        setEditingEntry({
                          section: "decisionsRequired",
                          index: null,
                          data: null,
                        });
                        setNewDecisions({
                          decisionTopic: "",
                          requiredByDate: "",
                          responsibility: "",
                          otherResponsibility: "",
                          remarks: "",
                        });
                        setShowDecisionsForm(true);
                      }}
                    >
                      Add New Entry
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Add/Edit Form */}
              {showDecisionsForm &&
                editingEntry.section === "decisionsRequired" && (
                  <Box
                    sx={{
                      border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      borderRadius: "8px",
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography sx={labelStyle}>
                        {editingEntry.index !== null
                          ? "Edit Decisions Required Entry"
                          : "Add Decisions Required Entry"}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setShowDecisionsForm(false);
                          setEditingEntry({
                            section: null,
                            index: null,
                            data: null,
                          });
                        }}
                        sx={{ color: COLORS.ERROR }}
                      >
                        <Icon name="close2" />
                      </IconButton>
                    </Box>
                    <Box display="flex" gap={2} mb={2}>
                      <Box flex={1.5}>
                        <Typography sx={labelStyle}>Decision Topic</Typography>
                        <TextField
                          fullWidth
                          placeholder="Decision topic"
                          size="small"
                          value={newDecisions.decisionTopic}
                          onChange={(e) =>
                            setNewDecisions({
                              ...newDecisions,
                              decisionTopic: e.target.value,
                            })
                          }
                          sx={inputStyle}
                        />
                      </Box>
                      <Box flex={1}>
                        <Typography sx={labelStyle}>
                          Required By Date
                        </Typography>
                        <TextField
                          fullWidth
                          type="date"
                          size="small"
                          value={newDecisions.requiredByDate}
                          onChange={(e) =>
                            setNewDecisions({
                              ...newDecisions,
                              requiredByDate: e.target.value,
                            })
                          }
                          sx={inputStyle}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    </Box>
                    <Box display="flex" gap={2} mb={2}>
                      <Box flex={1}>
                        <Typography sx={labelStyle}>Responsibility</Typography>
                        <TextField
                          fullWidth
                          select
                          size="small"
                          value={newDecisions.responsibility}
                          onChange={(e) =>
                            setNewDecisions({
                              ...newDecisions,
                              responsibility: e.target.value,
                            })
                          }
                          sx={inputStyle}
                        >
                          {responsibilityOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                        {newDecisions.responsibility === "Other" && (
                          <TextField
                            fullWidth
                            placeholder="Specify designation / responsibility"
                            size="small"
                            value={newDecisions.otherResponsibility || ""}
                            onChange={(e) =>
                              setNewDecisions({
                                ...newDecisions,
                                otherResponsibility: e.target.value,
                              })
                            }
                            sx={{ ...inputStyle, mt: 1 }}
                          />
                        )}
                      </Box>
                      <Box flex={1.5}>
                        <Typography sx={labelStyle}>Remark</Typography>
                        <TextField
                          fullWidth
                          placeholder="Remarks"
                          size="small"
                          value={newDecisions.remarks}
                          onChange={(e) =>
                            setNewDecisions({
                              ...newDecisions,
                              remarks: e.target.value,
                            })
                          }
                          sx={inputStyle}
                        />
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                      <Button
                        variant="outlined"
                        startIcon={<Icon name="delete" />}
                        sx={clearBtnStyle}
                        onClick={() => {
                          if (clearNewDecisions) clearNewDecisions();
                          setNewDecisions({
                            decisionTopic: "",
                            requiredByDate: "",
                            responsibility: "",
                            otherResponsibility: "",
                            remarks: "",
                          });
                        }}
                      >
                        Clear Fields
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={
                          editingEntry.section === "decisionsRequired" ? (
                            <Icon name="edit" />
                          ) : (
                            <Icon name="plus2" />
                          )
                        }
                        sx={addBtnStyle}
                        onClick={
                          editingEntry.section === "decisionsRequired"
                            ? handleUpdateEntry
                            : handleAddDecisions
                        }
                      >
                        {editingEntry.section === "decisionsRequired"
                          ? "Update Entry"
                          : "Add Entry"}
                      </Button>
                    </Box>
                  </Box>
                )}
            </Box>

            {/* ================= 14. RFI LOGS ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                14. RFI Logs
              </Typography>

              {/* Table */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    RFI No.
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Description
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Issue Date
                  </Box>
                  <Box
                    flex={0.8}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Status
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Expected Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Remark
                  </Box>
                  <Box
                    width="80px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.rfiLogs?.length > 0 ? (
                  mprData.rfiLogs.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={0.8}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.rfiNo}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.description}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.issueDate
                          ? format(new Date(row.issueDate), "dd/MM/yyyy")
                          : ""}
                      </Box>
                      <Box
                        flex={0.8}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color:
                            row.status === "Open"
                              ? COLORS.ERROR
                              : COLORS.SUCCESS,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          fontWeight: "bold",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.status}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.expectedDate
                          ? format(new Date(row.expectedDate), "dd/MM/yyyy")
                          : ""}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                      <Box
                        width="80px"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleEditEntry("rfiLogs", index)}
                          sx={{ color: "#8CD47E" }}
                        >
                          <Icon name="edit" size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEntry("rfiLogs", index)}
                          sx={{ color: COLORS.ERROR }}
                        >
                          <Icon name="close2" size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No RFI logs yet.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Row */}
                {!showRFIForm && (
                  <Box
                    display="flex"
                    sx={{ py: 0.8, justifyContent: "flex-end" }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={() => {
                        setEditingEntry({
                          section: "rfiLogs",
                          index: null,
                          data: null,
                        });
                        setNewRFI({
                          rfiNo: "",
                          description: "",
                          issueDate: "",
                          status: "Open",
                          expectedDate: "",
                          remarks: "",
                        });
                        setShowRFIForm(true);
                      }}
                    >
                      Add New Entry
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Add/Edit Form */}
              {showRFIForm && editingEntry.section === "rfiLogs" && (
                <Box
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      {editingEntry.index !== null
                        ? "Edit RFI Entry"
                        : "Add RFI Entry"}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowRFIForm(false);
                        setEditingEntry({
                          section: null,
                          index: null,
                          data: null,
                        });
                      }}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={0.8}>
                      <Typography sx={labelStyle}>RFI No.</Typography>
                      <TextField
                        fullWidth
                        placeholder="RFI #"
                        size="small"
                        value={newRFI.rfiNo}
                        onChange={(e) =>
                          setNewRFI({ ...newRFI, rfiNo: e.target.value })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1.5}>
                      <Typography sx={labelStyle}>Description</Typography>
                      <TextField
                        fullWidth
                        placeholder="RFI description"
                        size="small"
                        value={newRFI.description}
                        onChange={(e) =>
                          setNewRFI({ ...newRFI, description: e.target.value })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Issue Date</Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        value={newRFI.issueDate}
                        onChange={(e) =>
                          setNewRFI({ ...newRFI, issueDate: e.target.value })
                        }
                        sx={inputStyle}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={0.8}>
                      <Typography sx={labelStyle}>Status</Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={newRFI.status}
                        onChange={(e) =>
                          setNewRFI({ ...newRFI, status: e.target.value })
                        }
                        sx={inputStyle}
                      >
                        <MenuItem value="Open">Open</MenuItem>
                        <MenuItem value="Closed">Closed</MenuItem>
                      </TextField>
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>
                        Expected Date{" "}
                        {newRFI.status === "Open" && (
                          <span style={{ color: COLORS.ERROR }}>*</span>
                        )}
                      </Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        value={newRFI.expectedDate}
                        onChange={(e) =>
                          setNewRFI({ ...newRFI, expectedDate: e.target.value })
                        }
                        sx={inputStyle}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                    <Box flex={1.5}>
                      <Typography sx={labelStyle}>Remark</Typography>
                      <TextField
                        fullWidth
                        placeholder="Remarks"
                        size="small"
                        value={newRFI.remarks}
                        onChange={(e) =>
                          setNewRFI({ ...newRFI, remarks: e.target.value })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={clearBtnStyle}
                      onClick={() => {
                        if (clearNewRFI) clearNewRFI();
                        setNewRFI({
                          rfiNo: "",
                          description: "",
                          issueDate: "",
                          status: "Open",
                          expectedDate: "",
                          remarks: "",
                        });
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={
                        editingEntry.index !== null ? (
                          <Icon name="edit" />
                        ) : (
                          <Icon name="plus2" />
                        )
                      }
                      sx={addBtnStyle}
                      onClick={
                        editingEntry.index !== null
                          ? handleUpdateEntry
                          : handleAddRFI
                      }
                    >
                      {editingEntry.index !== null
                        ? "Update Entry"
                        : "Add Entry"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ================= 15. LOOK AHEAD PLAN (NEXT MONTH) ================= */}
            <Box mt={4}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.H3,
                  color: COLORS.PRIMARY,
                  fontFamily: FONT_FAMILY,
                  mb: 2,
                }}
              >
                15. Look Ahead Plan (Next Month)
              </Typography>

              {/* Table - Simple like WMR */}
              <Box mb={1.5}>
                {/* Table Header */}
                <Box
                  display="flex"
                  sx={{
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    py: 0.8,
                  }}
                >
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Activity
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Start Date
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    End Date
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Resource Requirement
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      px: 1,
                    }}
                  >
                    Remarks
                  </Box>
                  <Box
                    width="100px"
                    sx={{
                      ...TYPOGRAPHY.H4,
                      fontFamily: FONT_FAMILY,
                      color: COLORS.TEXT,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Box>
                </Box>

                {/* Table Body */}
                {mprData.lookAheadPlan?.length > 0 ? (
                  mprData.lookAheadPlan.map((row, index) => (
                    <Box
                      key={index}
                      display="flex"
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        py: 1,
                        minHeight: "60px",
                      }}
                    >
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.activity}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.startDate
                          ? format(new Date(row.startDate), "dd/MM/yyyy")
                          : ""}
                      </Box>
                      <Box
                        flex={1}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.endDate
                          ? format(new Date(row.endDate), "dd/MM/yyyy")
                          : ""}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.resourceRequirement}
                      </Box>
                      <Box
                        flex={1.5}
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          fontFamily: FONT_FAMILY,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          minHeight: "40px",
                        }}
                      >
                        {row.remarks}
                      </Box>
                      <Box
                        width="100px"
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleEditEntry("lookAheadPlan", index)
                          }
                          sx={{ color: "#8CD47E" }}
                        >
                          <Icon name="edit" size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleDeleteEntry("lookAheadPlan", index)
                          }
                          sx={{ color: COLORS.ERROR }}
                        >
                          <Icon name="close2" size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    display="flex"
                    sx={{
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      py: 2,
                      justifyContent: "center",
                      minHeight: "60px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT_FAMILY,
                        color: COLORS.LIGHT_SECONDARY,
                        fontStyle: "italic",
                      }}
                    >
                      No entries yet.
                    </Typography>
                  </Box>
                )}

                {/* Add New Entry Row */}
                {!showLookAheadForm && (
                  <Box
                    display="flex"
                    sx={{
                      py: 0.8,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon name="plus2" />}
                      sx={{
                        borderRadius: "7px",
                        textTransform: "none",
                        backgroundColor: COLORS.SECONDARY,
                        color: "#fff",
                        fontFamily: FONT_FAMILY,
                        px: 2.5,
                        "&:hover": {
                          backgroundColor: COLORS.PRIMARY,
                        },
                      }}
                      onClick={() => {
                        setEditingEntry({
                          section: null,
                          index: null,
                          data: null,
                        });
                        setNewLookAhead({
                          activity: "",
                          startDate: "",
                          endDate: "",
                          resourceRequirement: "",
                          remarks: "",
                        });
                        setShowLookAheadForm(true);
                      }}
                    >
                      Add New Entry
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Add Form */}
              {showLookAheadForm && (
                <Box
                  sx={{
                    border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "8px",
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography sx={labelStyle}>
                      Add Look Ahead Plan Entry
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setShowLookAheadForm(false)}
                      sx={{ color: COLORS.ERROR }}
                    >
                      <Icon name="close2" />
                    </IconButton>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1.5}>
                      <Typography sx={labelStyle}>Activity</Typography>
                      <TextField
                        fullWidth
                        placeholder="Activity description"
                        size="small"
                        value={newLookAhead.activity}
                        onChange={(e) =>
                          setNewLookAhead({
                            ...newLookAhead,
                            activity: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Start Date</Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        value={newLookAhead.startDate}
                        onChange={(e) =>
                          setNewLookAhead({
                            ...newLookAhead,
                            startDate: e.target.value,
                          })
                        }
                        sx={inputStyle}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>End Date</Typography>
                      <TextField
                        fullWidth
                        type="date"
                        size="small"
                        value={newLookAhead.endDate}
                        onChange={(e) =>
                          setNewLookAhead({
                            ...newLookAhead,
                            endDate: e.target.value,
                          })
                        }
                        sx={inputStyle}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={2} mb={2}>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>
                        Resource Requirement
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Resources needed"
                        size="small"
                        value={newLookAhead.resourceRequirement}
                        onChange={(e) =>
                          setNewLookAhead({
                            ...newLookAhead,
                            resourceRequirement: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                    <Box flex={1}>
                      <Typography sx={labelStyle}>Remarks</Typography>
                      <TextField
                        fullWidth
                        placeholder="Additional remarks"
                        size="small"
                        value={newLookAhead.remarks}
                        onChange={(e) =>
                          setNewLookAhead({
                            ...newLookAhead,
                            remarks: e.target.value,
                          })
                        }
                        sx={inputStyle}
                      />
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Icon name="delete" />}
                      sx={clearBtnStyle}
                      onClick={() => {
                        if (clearNewLookAhead) clearNewLookAhead();
                        setNewLookAhead({
                          activity: "",
                          startDate: "",
                          endDate: "",
                          resourceRequirement: "",
                          remarks: "",
                        });
                      }}
                    >
                      Clear Fields
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Icon name="plus2" />}
                      sx={addBtnStyle}
                      onClick={
                        editingEntry.section === "lookAheadPlan"
                          ? handleUpdateEntry
                          : handleAddLookAhead
                      }
                    >
                      {editingEntry.section === "lookAheadPlan"
                        ? "Update Entry"
                        : "Add Entry"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
            <Box mt={4}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography
                  sx={{
                    ...TYPOGRAPHY.H3,
                    fontFamily: FONT_FAMILY,
                    color: COLORS.PRIMARY,
                  }}
                >
                  16. Photographic Documentation
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<Icon name="plus2" />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontFamily: FONT_FAMILY,
                    backgroundColor: COLORS.SECONDARY,
                    color: "#fff",
                    px: 2.5,
                    "&:hover": {
                      backgroundColor: COLORS.SECONDARY,
                    },
                  }}
                  onClick={() => setShowPhotoUploadDialog(true)}
                >
                  Add Photo
                </Button>
              </Box>

              {/* Photo Grid - FIXED: No background, just image with data below, 2 per row */}
              {uploadedPhotos.length > 0 ? (
                <Grid container spacing={2} mb={3}>
                  {uploadedPhotos.map((photo, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {/* Image Container - No Card, no Paper, just image */}
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            height: "250px",
                            overflow: "hidden",
                            borderRadius: "8px",
                            backgroundColor: "#f5f5f5",
                          }}
                        >
                          <img
                            src={
                              photo.imageUrl ||
                              (photo.file
                                ? URL.createObjectURL(photo.file)
                                : "")
                            }
                            alt={photo.title || photo.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />

                          {/* Remove button overlay */}
                          <IconButton
                            size="small"
                            onClick={() => removePhoto(index)}
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                              color: COLORS.ERROR,
                              "&:hover": { backgroundColor: "#ffebee" },
                            }}
                          >
                            <Icon name="close2" />
                          </IconButton>
                        </Box>

                        {/* Data below the image */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily: FONT_FAMILY,
                              color: COLORS.TEXT,
                              fontWeight: "medium",
                              fontSize: "16px",
                              lineHeight: 1.2,
                            }}
                          >
                            {photo.title || photo.name}
                          </Typography>
                          {/* 
                          {photo.shapes && photo.shapes.length > 0 && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: FONT_FAMILY,
                                color: COLORS.SECONDARY,
                              }}
                            >
                              {photo.shapes.length} annotation{photo.shapes.length !== 1 ? 's' : ''}
                            </Typography>
                          )} */}

                          {photo.uploadedAt && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: FONT_FAMILY,
                                color: COLORS.LIGHT_SECONDARY,
                                fontSize: "12px",
                              }}
                            >
                              {format(
                                new Date(photo.uploadedAt),
                                "MMM dd, yyyy",
                              )}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 8,
                    border: `2px dashed ${COLORS.LIGHT_SECONDARY}`,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Icon name="image" size={48} color={COLORS.LIGHT_SECONDARY} />
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      color: COLORS.LIGHT_SECONDARY,
                      mt: 2,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    No photos added yet
                  </Typography>
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.LIGHT_SECONDARY,
                      mt: 1,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    Click "Add Photo with Annotations" to start adding photos
                    with drawing tools
                  </Typography>
                </Box>
              )}

              {/* Photo Upload Dialog */}
              <PhotoUploadWithDrawing
                open={showPhotoUploadDialog}
                onClose={() => setShowPhotoUploadDialog(false)}
                onSave={handlePhotoUploadWithDrawing}
                maxPhotos={10}
                persistenceKey={`mpr-photo-draft-${persistenceKey}`}
              />
            </Box>

            {/* ================= SUBMIT BUTTON ================= */}
            <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={3}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Icon name="delete" />}
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: "10px",
                  fontFamily: FONT_FAMILY,
                  color: COLORS.ERROR,
                  borderColor: COLORS.ERROR,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: `${COLORS.ERROR}15`,
                    borderColor: COLORS.ERROR,
                  },
                }}
                onClick={() => {
                  const monthStr = monthStartDate ? format(monthStartDate, "yyyy-MM") : "no-month";
                  const keysToRemove = [];
                  for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    // Match MPR main form and all sub-forms related to this project and month
                    if (key && (key.includes(`mpr-form-${projectId}-${monthStr}`) || key.includes(`mpr-`))) {
                      // Be careful not to clear other things if they share the prefix, but usually mpr- is specific enough
                      if (key.includes(`mpr-form-${projectId}`) || key.includes(`-mpr-`)) {
                         keysToRemove.push(key);
                      }
                    }
                  }
                  keysToRemove.forEach(k => localStorage.removeItem(k));
                  setToast({
                    open: true,
                    type: "info",
                    title: "Drafts Cleared",
                    message: "All monthly report drafts have been cleared.",
                  });
                  setTimeout(() => window.location.reload(), 1500);
                }}
                disabled={saving}
              >
                Clear All Drafts
              </Button>

              <Button
                variant="contained"
                size="large"
                startIcon={
                  saving ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Icon name="save" />
                  )
                }
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: "10px",
                  fontFamily: FONT_FAMILY,
                  backgroundColor: COLORS.PRIMARY,
                  textTransform: "none",
                  "&:disabled": {
                    backgroundColor: COLORS.LIGHT_SECONDARY,
                  },
                }}
                onClick={handleSaveMPR}
                disabled={saving}
              >
                {saving ? "Saving..." : "SUBMIT MONTHLY PROGRESS REPORT"}
              </Button>
            </Box>
          </>
        )}

        {/* Month Picker Dialog */}
        <Dialog
          open={showMonthPicker}
          onClose={() => setShowMonthPicker(false)}
        >
          <DialogTitle>Select Month</DialogTitle>
          <DialogContent>
            <DatePicker
              value={selectedMonth}
              onChange={handleMonthSelect}
              openTo="month"
              views={["year", "month"]}
              minDate={
                userData?.role === "ENGINEER"
                  ? startOfMonth(new Date())
                  : undefined
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  sx={{
                    position: "absolute",
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                  InputProps={{
                    ...params.InputProps,
                    readOnly: true,
                  }}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowMonthPicker(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* Toast Component */}
        <AppToast
          open={toast.open}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={closeToast}
        />
      </Box>
    </LocalizationProvider>
  );
}

/* ================= STYLES ================= */

const labelStyle = {
  ...TYPOGRAPHY.H4,
  color: COLORS.PRIMARY,
  mb: 0.5,
  fontFamily: FONT_FAMILY,
};

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontFamily: FONT_FAMILY,
    "& fieldset": {
      borderColor: COLORS.LIGHT_SECONDARY,
    },
    "&:hover fieldset": {
      borderColor: COLORS.SECONDARY,
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.SECONDARY,
    },
  },
  "& input::placeholder, & textarea::placeholder": {
    color: COLORS.LIGHT_SECONDARY,
    fontSize: "13px",
  },
};

const addBtnStyle = {
  borderRadius: "7px",
  textTransform: "none",
  backgroundColor: COLORS.SECONDARY,
  color: "#fff",
  fontFamily: FONT_FAMILY,
  px: 2.5,
  "&:hover": {
    backgroundColor: COLORS.PRIMARY,
  },
};

const clearBtnStyle = {
  borderRadius: "7px",
  textTransform: "none",
  color: COLORS.ERROR,
  borderColor: COLORS.ERROR,
  fontFamily: FONT_FAMILY,
  "&:hover": {
    backgroundColor: `${COLORS.ERROR}1A`,
    borderColor: COLORS.ERROR,
  },
};
